import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet.vectorgrid';
import { airspaceApi, environmentalApi, dfsAirspaceApi, dipulApi, openAipApi, dgacApi } from '../../lib/api';
import { Search, MapPin, Loader2, Leaf, Mountain, TreePine, Bird, Shield, Plane, Maximize2, Minimize2, X, Building2, CircleDot, Layers } from 'lucide-react';
import MapLayersPanel from './MapLayersPanel';
import MapOnboardingHint, { hasDismissedOnboarding } from './MapOnboardingHint';
import NotamPanel from '../NotamPanel';
import { useOpenAipRestrictionLayer } from './useOpenAipRestrictionLayer';
import { trackEvent } from '../../lib/analytics';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ZONE_STYLES = {
  ctr: { color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.2, weight: 2 },
  tma: { color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.15, weight: 2 },
  restricted: { color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.25, weight: 2 },
  prohibited: { color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.3, weight: 2 },
  danger: { color: '#f97316', fillColor: '#f97316', fillOpacity: 0.2, weight: 2, dashArray: '5, 5' },
};

// Environmental zone styles
const ENV_ZONE_STYLES = {
  natura2000_habitat: { color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.2, weight: 2 },
  natura2000_oiseaux: { color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.2, weight: 2, dashArray: '4, 4' },
  reserve_naturelle: { color: '#eab308', fillColor: '#eab308', fillOpacity: 0.25, weight: 2 },
  parc_national: { color: '#dc2626', fillColor: '#dc2626', fillOpacity: 0.15, weight: 3 },
  parc_regional: { color: '#f97316', fillColor: '#f97316', fillOpacity: 0.1, weight: 2, dashArray: '6, 3' },
};

// Configuration centralisée des couches WMS officielles européennes
import {
  GEOPF_WMTS_URL, DGAC_DRONE_LAYER,
  DIPUL_WMS_URL, DIPUL_LAYERS,
  DFS_INSPIRE_WMS_URL, DFS_AIRSPACE_LAYER,
  BELGIUM_WMS_URL, BELGIUM_AOEM_LAYER,
  LUXEMBOURG_WMS_URL, LUXEMBOURG_UAS_LAYERS,
  NETHERLANDS_WMS_URL, NETHERLANDS_UAS_LAYERS,
  SWITZERLAND_TILES_URL,
  AUSTRIA_WMS_URL, AUSTRIA_AIRSPACE_LAYER,
  SPAIN_ENAIRE_WMS_URL, SPAIN_ENAIRE_LAYERS,
  ICELAND_WMS_URL, ICELAND_DRONE_LAYERS,
  OPENAIP_TILES_URL,
  GEOCODE_API,
  VECTOR_TILES_URL,
  SUPPORTED_COUNTRIES,
} from './mapLayers.config';

// Icône personnalisée pour le point de décollage
const takeoffIcon = L.divIcon({
  html: `<div style="
    width: 36px; height: 36px; 
    background: linear-gradient(135deg, #10b981, #059669); 
    border-radius: 50% 50% 50% 0; 
    transform: rotate(-45deg);
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex; align-items: center; justify-content: center;
  ">
    <span style="transform: rotate(45deg); font-size: 16px;">🛫</span>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  className: 'takeoff-marker'
});

export const FlightMap = ({ 
  onPolygonDrawn, 
  onTakeoffPointSet,
  onMapReady = null, // Callback pour exposer l'instance de carte Leaflet
  initialPolygon = null,
  initialTakeoffPoint = null,
  showZones = true,
  editable = true,
  height = '650px',
  compactMode = false,  // Mode compact pour les cartes de lecture (moins de contrôles)
  demoMode = false,     // Mode démo (Landing) — active toutes les couches pays par défaut
  userCountry = null    // ISO2 code (e.g. 'fr') — pre-centre on user's country if no ?territory= override
}) => {
  // Country → default-active layers. Two independent triggers:
  //   • `demoMode` (landing map) → only the cross-border European airspace
  //     (OpenAIP + NOTAMs/CTR/TMA/P/R/D sub-toggles) is enabled. This
  //     keeps the landing preview readable without swamping it with
  //     every country's zones.
  //   • Authenticated user with `userCountry` on their profile → their
  //     national ED-269 / IGN / DFS / etc. layers auto-activate so the
  //     first-time map isn't blank.
  // Country-specific layers therefore key on `uc === flag` ONLY (not
  // demoMode). Cross-border layers key on `demoMode || Boolean(uc)`.
  const uc = (userCountry || '').toLowerCase();
  const cd = (flag) => uc === flag;
  const anyUserContext = demoMode || Boolean(uc);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const drawnItems = useRef(null);
  const zonesLayer = useRef(null);
  const ignWmsLayer = useRef(null);
  const drawControlRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawingRef = useRef(false); // Ref to track drawing mode in real-time
  const takeoffMarkerRef = useRef(null); // Ref for takeoff point marker
  const isTakeoffModeRef = useRef(false); // Ref to track takeoff mode in real-time
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTakeoffMode, setIsTakeoffMode] = useState(false); // Mode for placing takeoff point
  const [takeoffPoint, setTakeoffPoint] = useState(initialTakeoffPoint); // Takeoff point coordinates
  const [takeoffAltitude, setTakeoffAltitude] = useState(null); // Max altitude at takeoff point
  const [takeoffLoading, setTakeoffLoading] = useState(false); // Loading state for altitude fetch
  const zoneLayers = useRef({
    ctr: null,
    tma: null,
    restricted: null,
    prohibited: null,
    danger: null,
    dji: null,
    environmental: null,
    dipul: null  // German DIPUL layer
  });
  const onPolygonDrawnRef = useRef(onPolygonDrawn);
  const onTakeoffPointSetRef = useRef(onTakeoffPointSet);
  const [zones, setZones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIgnLayer, setShowIgnLayer] = useState(cd('fr')); // French IGN/DGAC — follows demoMode
  const [showDipulLayer, setShowDipulLayer] = useState(false); // DEPRECATED: replaced by ED-318 vector sync (DIPUL). Kept as no-op state to avoid breaking the click-info paths that still reference it — the toggle row was removed from MapLayersPanel.
  const [showDfsAirspaceLayer, setShowDfsAirspaceLayer] = useState(cd('de')); // German airspace (CTR/TMA)
  const [showBelgiumLayer, setShowBelgiumLayer] = useState(cd('be')); // Belgian AOEM restrictions
  const [showLuxembourgLayer, setShowLuxembourgLayer] = useState(cd('lu')); // Luxembourg DAC UAS zones
  const [showNetherlandsLayer, setShowNetherlandsLayer] = useState(cd('nl')); // Netherlands LVNL drone no-fly zones
  const [showSwitzerlandLayer, setShowSwitzerlandLayer] = useState(cd('ch')); // Switzerland FOCA/BAZL UAS restrictions
  const [showAustriaLayer, setShowAustriaLayer] = useState(cd('at')); // Austria Austro Control airspace
  const [showIcelandLayer, setShowIcelandLayer] = useState(cd('is')); // Iceland Samgöngustofa drone zones
  const [showCzechiaLayer, setShowCzechiaLayer] = useState(cd('cz')); // Czechia AIM CR / ÚCL UAS zones (ED-318)
  const [showSlovakiaLayer, setShowSlovakiaLayer] = useState(cd('sk')); // Slovakia LPS SR airspace zones (ED-269)
  const [showDenmarkLayer, setShowDenmarkLayer] = useState(cd('dk')); // Denmark Trafikstyrelsen UAS zones (ED-269)
  const [showIrelandLayer, setShowIrelandLayer] = useState(cd('ie')); // Ireland IAA UAS zones (ED-269)
  const [showEstoniaLayer, setShowEstoniaLayer] = useState(cd('ee')); // Estonia Transpordiamet/EANS UAS zones (ED-269)
  const [showCyprusLayer, setShowCyprusLayer] = useState(cd('cy')); // Cyprus DCA UAS zones (ED-269)
  // ED-269 official UAS Geographical Zones (imported via admin).
  // Enabled only when the user has a country on their profile — pros
  // expect their national ED-269 zones to be visible right away. Skipped
  // in demo mode to keep the landing map from looking like a spaghetti.
  const [showEd269Layer, setShowEd269Layer] = useState(Boolean(uc));
  // Pre-select the user's country so ticking the master ED-269 toggle
  // actually shows something. In demo mode we keep FR selected in the
  // set so ticking the master switch manually on the landing still
  // reveals France zones (matches the previous UX).
  const [ed269Country, setEd269Country] = useState(() => {
    if (uc) return new Set([uc]);
    if (demoMode) return new Set(['fr']);
    return new Set();
  });
  // Convenience: toggle a country in/out of the active ED-269 set. Used by
  // the layer panel rows so the user can stack several countries on the map.
  const toggleEd269Country = useCallback((cc) => {
    setEd269Country((prev) => {
      const next = new Set(prev);
      if (next.has(cc)) next.delete(cc);
      else next.add(cc);
      return next;
    });
  }, []);
  const [ed269Loading, setEd269Loading] = useState(false);
  const [ed269Available, setEd269Available] = useState([]); // [{country: 'FR', count: 7107}, ...]
  // Filter applied when fetching the ED-269/ED-318 layer (ALL / AERO / IMPORTANT)
  //   'all'        → tout (peut être lourd pour ES = 15 795 zones)
  //   'aero'       → catégorie AERONAUTICAL seulement (bandes d'aéroport)
  //   'important'  → min_risk=4 = PROHIBITED + REQ_AUTHORISATION (toutes catégories)
  const [ed269Filter, setEd269Filter] = useState('important');
  // OpenAIP (European CTR/TMA/P/D/R) is the ONE layer that stays on in
  // demo mode — per user feedback, the landing preview should showcase
  // European airspace without loading every national layer on top.
  // Also enabled for authenticated users regardless of country.
  const [showOpenAipLayer, setShowOpenAipLayer] = useState(anyUserContext);
  const [showItalyLayer, setShowItalyLayer] = useState(cd('it')); // Italy (uses OpenAIP)
  // NOTAM overlay — pros consider it table-stakes, so ON as soon as we
  // have a country context. Off on the anonymous landing (`demoMode`
  // alone doesn't trigger it) to keep the preview uncluttered.
  const [showNotamsLayer, setShowNotamsLayer] = useState(Boolean(uc));
  const [mapCenterForNotams, setMapCenterForNotams] = useState(null);
  // Italy — dedicated "Restriction drone" overlay (PROHIBITED / RESTRICTED / DANGER / CTR)
  // sourced from OpenAIP, rendered with the colour code the user expects:
  //   Rouge=Prohibited, Orange=Restricted, Jaune=Danger, Bleu=CTR.
  const [showItalyDroneRestrictions, setShowItalyDroneRestrictions] = useState(cd('it'));
  // Sweden — OpenAIP "Restriction drone" overlay, same UX as Italy.
  // (LFV's official Drönarkartan is CC BY-NC-ND so we use OpenAIP for now
  //  while waiting for a commercial license from LFV.)
  const [showSwedenDroneRestrictions, setShowSwedenDroneRestrictions] = useState(cd('se'));
  // Finland — same pattern as Italy/Sweden. OpenAIP source (Traficom only
  // publishes a CSV and Fintraffic Sky doesn't expose a public GeoJSON API).
  const [showFinlandDroneRestrictions, setShowFinlandDroneRestrictions] = useState(cd('fi'));
  // Belgium — same pattern. The official Skeyes "droneguide.be" platform is
  // browser-only (Unifly WFS behind a session) so we use OpenAIP for an
  // open, queryable vector overlay (158 zones: 1 PROHIBITED + 123 RESTRICTED
  // + 23 DANGER + 11 CTR). The NGI raster WMS stays available as a
  // complementary visual reference.
  const [showBelgiumDroneRestrictions, setShowBelgiumDroneRestrictions] = useState(cd('be'));
  // Netherlands / Poland / Norway — OpenAIP overlays (same pattern as BE).
  // LVNL's PDOK GeoServer feeds raster tiles only; PANSA's DroneTower is
  // session-authed; Avinor's NIPS is browser-only. OpenAIP is the only
  // permissive, queryable vector source for these three.
  const [showNetherlandsDroneRestrictions, setShowNetherlandsDroneRestrictions] = useState(cd('nl'));
  const [showPolandDroneRestrictions, setShowPolandDroneRestrictions] = useState(cd('pl'));
  const [showNorwayDroneRestrictions, setShowNorwayDroneRestrictions] = useState(cd('no'));
  // Eastern / Southern Europe — OpenAIP only, no public ED-269 feed available.
  const [showCroatiaDroneRestrictions, setShowCroatiaDroneRestrictions] = useState(cd('hr'));
  const [showGreeceDroneRestrictions, setShowGreeceDroneRestrictions] = useState(cd('gr'));
  const [showBulgariaDroneRestrictions, setShowBulgariaDroneRestrictions] = useState(cd('bg'));
  const [showHungaryDroneRestrictions, setShowHungaryDroneRestrictions] = useState(cd('hu'));
  const [showSloveniaDroneRestrictions, setShowSloveniaDroneRestrictions] = useState(cd('si'));
  const [showSpainLayer, setShowSpainLayer] = useState(cd('es')); // Spain (uses OpenAIP)
  const [showDjiLayer, setShowDjiLayer] = useState(false); // DJI zones - désactivé par défaut
  
  // DroneKeeper POI layers
  const [showHospitalsLayer, setShowHospitalsLayer] = useState(false); // Hospitals/Heliports
  const [showAirportsLayer, setShowAirportsLayer] = useState(false); // Airports locations
  const [showAirportsPPPLayer, setShowAirportsPPPLayer] = useState(false); // Airport protection perimeters
  const [showHelistationsLayer, setShowHelistationsLayer] = useState(false); // Helistations
  
  // DroneKeeper Airspace layers
  const [showDkCtrLayer, setShowDkCtrLayer] = useState(false); // CTR - Control zones
  const [showDkTmaLayer, setShowDkTmaLayer] = useState(false); // TMA - Terminal areas
  const [showDkAtzLayer, setShowDkAtzLayer] = useState(false); // ATZ - Aerodrome traffic zones
  const [showDkPLayer, setShowDkPLayer] = useState(false); // P - Prohibited zones
  const [showDkRLayer, setShowDkRLayer] = useState(false); // R - Restricted zones
  const [showDkDLayer, setShowDkDLayer] = useState(false); // D - Dangerous zones
  const [djiZones, setDjiZones] = useState([]);
  const [isDrawingMode, setIsDrawingMode] = useState(false); // Track if user is drawing
  // FR territory sub-layer filters — these flags drive the standalone
  // French WMTS overlays (CTR / TMA / Restricted / Prohibited / Danger).
  // We mirror demoMode so the landing stays visually empty by default;
  // the user enables individual categories from the layers panel.
  // OpenAIP airspace sub-layers (CTR/TMA/P/R/D). Follow the master
  // OpenAIP toggle — enabled for demo AND for authenticated users so
  // the 5 categories are visible from the get-go.
  const [layerVisibility, setLayerVisibility] = useState({
    ctr: anyUserContext,
    tma: anyUserContext,
    restricted: anyUserContext,
    prohibited: anyUserContext,
    danger: anyUserContext,
  });
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [mapStyle, setMapStyle] = useState('standard'); // 'standard' or 'satellite'

  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  // Onboarding hint — only relevant when the map is otherwise empty on
  // load. Skip it when demo mode has the European airspace layer active,
  // when a userCountry is known (national layers auto-activate), or when
  // the user has already dismissed it in this browser.
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => demoMode || Boolean(uc) || hasDismissedOnboarding(),
  );
  // Permanently dismiss the onboarding hint the first time the user opens
  // the layers panel — they've discovered the feature, no need to nag.
  useEffect(() => {
    if (layersPanelOpen && !onboardingDismissed) {
      setOnboardingDismissed(true);
      try { localStorage.setItem('dronemaps_onboarding_dismissed', '1'); } catch { /* private mode */ }
    }
  }, [layersPanelOpen, onboardingDismissed]);
  // Fire `map_opened` once per component mount so the funnel can measure
  // how many signed-up users ever reach the interactive map. `compactMode`
  // maps to embedded read-only mini-maps (mission detail thumbnail) so we
  // skip them to keep the signal focused on primary exploration.
  useEffect(() => {
    if (!compactMode) {
      trackEvent('map_opened', {
        context: demoMode ? 'landing_demo' : 'app',
        country: userCountry || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const baseTileLayer = useRef(null);
  const dipulLayerRef = useRef(null); // Reference to DIPUL WMS layer
  const dfsAirspaceLayerRef = useRef(null); // Reference to DFS INSPIRE airspace layer
  const belgiumLayerRef = useRef(null); // Reference to Belgian AOEM WMS layer
  const luxembourgLayerRef = useRef(null); // Reference to Luxembourg DAC UAS WMS layer
  const netherlandsLayerRef = useRef(null); // Reference to Netherlands LVNL WMS layer
  const switzerlandLayerRef = useRef(null); // Reference to Switzerland FOCA WMTS layer
  const austriaLayerRef = useRef(null); // Reference to Austria Austro Control WMS layer
  const spainEnaireLayerRef = useRef(null); // Reference to Spain ENAIRE UAS Zones WMS layer (ED-318)
  const icelandLayerRef = useRef(null); // Reference to Iceland Samgöngustofa WMS layer
  // ED-269 official UAS zones — one Leaflet layer per active country so the
  // user can stack several countries on the map (e.g. Portugal + Spain near
  // the border). Shape: { [cc]: { layer: L.GeoJSON, filter: 'all' | 'aero' | 'important' } }
  const ed269LayersRef = useRef({});
  const italyDroneRestrictionsLayerRef = useRef(null); // OpenAIP Italy drone restriction overlay (L.geoJSON)
  const swedenDroneRestrictionsLayerRef = useRef(null); // OpenAIP Sweden drone restriction overlay (L.geoJSON)
  const finlandDroneRestrictionsLayerRef = useRef(null); // OpenAIP Finland drone restriction overlay (L.geoJSON)
  const belgiumDroneRestrictionsLayerRef = useRef(null); // OpenAIP Belgium drone restriction overlay (L.geoJSON)
  const netherlandsDroneRestrictionsLayerRef = useRef(null); // OpenAIP Netherlands drone restriction overlay
  const polandDroneRestrictionsLayerRef = useRef(null); // OpenAIP Poland drone restriction overlay
  const norwayDroneRestrictionsLayerRef = useRef(null); // OpenAIP Norway drone restriction overlay
  const croatiaDroneRestrictionsLayerRef = useRef(null);
  const greeceDroneRestrictionsLayerRef = useRef(null);
  const bulgariaDroneRestrictionsLayerRef = useRef(null);
  const hungaryDroneRestrictionsLayerRef = useRef(null);
  const sloveniaDroneRestrictionsLayerRef = useRef(null);
  const openAipLayerRef = useRef(null); // Reference to OpenAIP airspace tile layer
  const natura2000WmsRef = useRef(null); // Reference to EEA Natura 2000 WMS layer (Europe-wide)
  
  // DroneKeeper vector tile layer refs
  const hospitalsLayerRef = useRef(null);
  const airportsLayerRef = useRef(null);
  const airportsPPPLayerRef = useRef(null);
  const helistationsLayerRef = useRef(null);
  
  // DroneKeeper airspace layer refs
  const dkCtrLayerRef = useRef(null);
  const dkTmaLayerRef = useRef(null);
  const dkAtzLayerRef = useRef(null);
  const dkPLayerRef = useRef(null);
  const dkRLayerRef = useRef(null);
  const dkDLayerRef = useRef(null);
  
  const [currentZoom, setCurrentZoom] = useState(6); // Track zoom level
  const [showNatura2000Wms, setShowNatura2000Wms] = useState(false); // Show EEA Natura 2000 WMS layer - désactivé par défaut
  
  // Environmental zones state
  const [environmentalZones, setEnvironmentalZones] = useState([]);
  const [showEnvironmentalLayers, setShowEnvironmentalLayers] = useState({
    natura2000_habitat: false, // Désactivé - on utilise Natura 2000 Europe (EEA) à la place
    natura2000_oiseaux: false,
    reserve_naturelle: false,
    parc_national: false,
    parc_regional: false
  });
  const [loadingEnvZones, setLoadingEnvZones] = useState(false);
  
  // DFS Airspace popup state
  const [airspacePopupLoading, setAirspacePopupLoading] = useState(false);
  const airspacePopupRef = useRef(null);
  
  // DIPUL Zone popup state
  const [dipulPopupLoading, setDipulPopupLoading] = useState(false);
  const dipulPopupRef = useRef(null);

  // Fullscreen mode - handle escape key and map resize
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Invalidate map size when fullscreen changes
    if (mapInstance.current) {
      setTimeout(() => {
        mapInstance.current.invalidateSize();
      }, 100);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // Recherche d'adresse via API adresse.data.gouv.fr
  const searchAddressAPI = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      // Utiliser Nominatim pour la recherche européenne (France + Allemagne)
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: SUPPORTED_COUNTRIES.join(','),
      });
      
      const response = await fetch(`${GEOCODE_API}?${params}`, {
        headers: {
          'Accept-Language': 'fr,de',
        }
      });
      const data = await response.json();
      
      // Convertir le format Nominatim vers un format compatible
      const results = data.map(item => ({
        properties: {
          label: item.display_name,
          name: item.name,
          city: item.address?.city || item.address?.town || item.address?.village,
          country: item.address?.country,
          countryCode: item.address?.country_code?.toUpperCase(),
        },
        geometry: {
          coordinates: [parseFloat(item.lon), parseFloat(item.lat)]
        }
      }));
      
      setSearchResults(results);
      setShowResults(true);
      trackEvent('map_search_performed', {
        query_len: query.length,
        results_count: results.length,
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Centrer la carte sur une adresse
  const goToAddress = useCallback((feature) => {
    if (!mapInstance.current || !feature) return;
    
    const [lng, lat] = feature.geometry.coordinates;
    
    // Supprimer l'ancien marqueur s'il existe
    if (searchMarkerRef.current) {
      mapInstance.current.removeLayer(searchMarkerRef.current);
    }
    
    // Créer un nouveau marqueur
    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'search-marker',
        html: `<div style="
          background: #ef4444;
          width: 24px;
          height: 24px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        "></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24]
      })
    }).addTo(mapInstance.current);
    
    marker.bindPopup(`<b>${feature.properties.label}</b>`).openPopup();
    searchMarkerRef.current = marker;
    
    // Centrer et zoomer sur l'adresse
    mapInstance.current.setView([lat, lng], 15);
    
    // Fermer les résultats
    setShowResults(false);
    setSearchAddress(feature.properties.label);
  }, []);

  // Handler combiné pour les infos allemandes (DFS + DIPUL) au clic
  const handleGermanInfoClick = useCallback(async (latlng, zoom) => {
    // Ne pas afficher les popups pendant le dessin
    if (isDrawingMode) return;
    
    // Vérifier si au moins une couche est active
    if (!showDfsAirspaceLayer && !showDipulLayer) return;
    
    setAirspacePopupLoading(true);
    setDipulPopupLoading(true);
    
    try {
      // Lancer les deux requêtes en parallèle
      const [dfsResponse, dipulResponse] = await Promise.allSettled([
        showDfsAirspaceLayer ? dfsAirspaceApi.getInfo(latlng.lat, latlng.lng, zoom) : Promise.resolve({ data: { features: [] } }),
        showDipulLayer ? dipulApi.getInfo(latlng.lat, latlng.lng, zoom) : Promise.resolve({ data: { features: [] } })
      ]);
      
      const dfsData = dfsResponse.status === 'fulfilled' ? dfsResponse.value.data : { features: [] };
      const dipulData = dipulResponse.status === 'fulfilled' ? dipulResponse.value.data : { features: [] };
      
      const hasDfsFeatures = dfsData.features && dfsData.features.length > 0;
      const hasDipulFeatures = dipulData.features && dipulData.features.length > 0;
      
      // Si aucune donnée, ne pas afficher de popup
      if (!hasDfsFeatures && !hasDipulFeatures) {
        return;
      }
      
      // Créer le contenu du popup combiné
      let popupContent = `
        <div style="min-width: 300px; max-width: 420px;">
          <h3 style="margin: 0 0 12px 0; color: #1e293b; font-size: 15px; font-weight: 700; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px;">
            🇩🇪 Informations Aériennes Allemagne
          </h3>
      `;
      
      // Section DFS - Espaces aériens
      if (hasDfsFeatures) {
        popupContent += `
          <div style="margin-bottom: 12px;">
            <div style="color: #4f46e5; font-size: 12px; font-weight: 600; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>✈️</span>
              <span>Espaces Aériens (DFS)</span>
            </div>
        `;
        
        const typeColors = {
          'CTR': '#0ea5e9',
          'TMA': '#8b5cf6',
          'ATZ': '#f59e0b',
          'FIR': '#6b7280',
          'UIR': '#6b7280',
          'restrictedArea': '#ef4444',
          'prohibitedArea': '#dc2626',
          'dangerArea': '#f97316'
        };
        
        dfsData.features.forEach((feature) => {
          const color = typeColors[feature.type] || '#4f46e5';
          popupContent += `
            <div style="padding: 8px; margin-bottom: 6px; background: #f8fafc; border-radius: 6px; border-left: 3px solid ${color};">
              <div style="font-weight: 600; color: #1e293b; font-size: 12px;">${feature.name || feature.id}</div>
              <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px;">
                <span style="background: ${color}20; color: ${color}; padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 500;">
                  ${feature.type}
                </span>
                ${feature.icao ? `<span style="background: #e0e7ff; color: #4338ca; padding: 1px 6px; border-radius: 10px; font-size: 10px;">${feature.icao}</span>` : ''}
              </div>
              <div style="font-size: 10px; color: #64748b; margin-top: 4px;">${feature.type_label}</div>
            </div>
          `;
        });
        popupContent += `</div>`;
      }
      
      // Section DIPUL - Zones de restriction drone
      if (hasDipulFeatures) {
        popupContent += `
          <div style="margin-bottom: 12px;">
            <div style="color: #d97706; font-size: 12px; font-weight: 600; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🚁</span>
              <span>Restrictions Drone (DIPUL)</span>
            </div>
        `;
        
        dipulData.features.forEach((feature) => {
          const color = feature.color || '#d97706';
          popupContent += `
            <div style="padding: 8px; margin-bottom: 6px; background: #fffbeb; border-radius: 6px; border-left: 3px solid ${color};">
              <div style="font-weight: 600; color: #1e293b; font-size: 12px; display: flex; align-items: center; gap: 4px;">
                <span>${feature.icon || '📍'}</span>
                <span>${feature.name || feature.type_label}</span>
              </div>
              <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px;">
                <span style="background: ${color}20; color: ${color}; padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 500;">
                  ${feature.type_label}
                </span>
                ${feature.type_label_de ? `<span style="background: #e5e7eb; color: #374151; padding: 1px 6px; border-radius: 10px; font-size: 10px;">${feature.type_label_de}</span>` : ''}
              </div>
              ${feature.restriction ? `
                <div style="font-size: 10px; color: #b45309; margin-top: 4px; padding: 4px; background: #fef3c7; border-radius: 4px;">
                  ⚠️ ${feature.restriction}
                </div>
              ` : ''}
            </div>
          `;
        });
        popupContent += `</div>`;
      }
      
      // Footer
      popupContent += `
          <div style="font-size: 9px; color: #94a3b8; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
            📍 ${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}<br/>
            Sources: ${hasDfsFeatures ? 'DFS INSPIRE' : ''}${hasDfsFeatures && hasDipulFeatures ? ' / ' : ''}${hasDipulFeatures ? 'DIPUL' : ''}
          </div>
        </div>
      `;
      
      // Fermer les popups existants et afficher le nouveau
      if (airspacePopupRef.current) {
        mapInstance.current.closePopup(airspacePopupRef.current);
      }
      if (dipulPopupRef.current) {
        mapInstance.current.closePopup(dipulPopupRef.current);
      }
      
      airspacePopupRef.current = L.popup({
        maxWidth: 450,
        className: 'german-info-popup'
      })
        .setLatLng(latlng)
        .setContent(popupContent)
        .openOn(mapInstance.current);
        
    } catch (error) {
      console.error('Error fetching German airspace/DIPUL info:', error);
    } finally {
      setAirspacePopupLoading(false);
      setDipulPopupLoading(false);
    }
  }, [showDfsAirspaceLayer, showDipulLayer, isDrawingMode]);

  // Handler pour les infos OpenAIP (espaces aériens européens) au clic
  const handleOpenAipInfoClick = useCallback(async (latlng, zoom) => {
    // Ne pas afficher les popups pendant le dessin
    if (isDrawingMode) return;
    
    // Vérifier si la couche OpenAIP est active
    if (!showOpenAipLayer) return;
    
    // Ne pas afficher si on est sur l'Allemagne (déjà couvert par DFS/DIPUL)
    const lat = latlng.lat;
    const lng = latlng.lng;
    const isInGermany = lat >= 47.3 && lat <= 55.0 && lng >= 5.9 && lng <= 15.0;
    if (isInGermany) return;
    
    try {
      const response = await openAipApi.getInfo(latlng.lat, latlng.lng, 15000);
      const data = response.data;
      
      if (!data.features || data.features.length === 0) {
        return;
      }
      
      // Créer le contenu du popup
      let popupContent = `
        <div style="min-width: 320px; max-width: 450px;">
          <h3 style="margin: 0 0 12px 0; color: #1e293b; font-size: 15px; font-weight: 700; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px;">
            ${data.country_name || '🇪🇺 Espaces Aériens Européens'}
          </h3>
          
          <!-- Zones aériennes -->
          <div style="margin-bottom: 10px;">
            <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 6px;">📍 ZONES DÉTECTÉES (${data.total})</div>
      `;
      
      data.features.slice(0, 5).forEach((feature) => {
        const color = feature.color || '#3b82f6';
        popupContent += `
          <div style="padding: 8px; margin-bottom: 6px; background: #f8fafc; border-radius: 6px; border-left: 3px solid ${color};">
            <div style="font-weight: 600; color: #1e293b; font-size: 12px;">${feature.name}</div>
            <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px;">
              <span style="background: ${color}20; color: ${color}; padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 500;">
                ${feature.type}
              </span>
              <span style="background: #e0e7ff; color: #4338ca; padding: 1px 6px; border-radius: 10px; font-size: 10px;">
                ${feature.icao_class}
              </span>
            </div>
            <div style="font-size: 10px; color: #374151; margin-top: 4px; display: flex; gap: 8px; flex-wrap: wrap;">
              <span>⬆️ ${feature.upper_limit}</span>
              <span>⬇️ ${feature.lower_limit}</span>
            </div>
            ${feature.frequencies && feature.frequencies.length > 0 ? `
              <div style="font-size: 10px; color: #0369a1; margin-top: 4px;">
                📻 ${feature.frequencies.join(', ')}
              </div>
            ` : ''}
          </div>
        `;
      });
      
      popupContent += `</div>`;
      
      // Ajouter les autorités à contacter
      if (data.authorities && data.authorities.length > 0) {
        popupContent += `
          <div style="margin-top: 12px; padding-top: 10px; border-top: 2px solid #e5e7eb;">
            <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 8px;">🏛️ AUTORITÉS À CONTACTER</div>
        `;
        
        data.authorities.forEach((auth) => {
          const contactColor = auth.contact_type === 'autorisation' ? '#dc2626' : '#2563eb';
          const contactBg = auth.contact_type === 'autorisation' ? '#fef2f2' : '#eff6ff';
          const contactLabel = auth.contact_type === 'autorisation' ? '🔴 Autorisation requise' : '🔵 Information';
          
          popupContent += `
            <div style="padding: 10px; margin-bottom: 8px; background: ${contactBg}; border-radius: 8px; border-left: 3px solid ${contactColor};">
              <div style="font-weight: 600; color: #1e293b; font-size: 12px;">${auth.authority}</div>
              <div style="font-size: 10px; color: #64748b; margin-top: 2px;">${auth.reason}</div>
              <div style="font-size: 10px; color: #374151; margin-top: 6px; line-height: 1.4;">${auth.details}</div>
              <div style="margin-top: 8px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
                <span style="background: ${contactColor}15; color: ${contactColor}; padding: 2px 8px; border-radius: 12px; font-size: 9px; font-weight: 600;">
                  ${contactLabel}
                </span>
                ${auth.phone ? `<span style="font-size: 10px; color: #374151;">📞 ${auth.phone}</span>` : ''}
              </div>
              ${auth.url ? `
                <a href="${auth.url}" target="_blank" rel="noopener noreferrer" 
                   style="display: inline-block; margin-top: 8px; padding: 6px 12px; background: ${contactColor}; color: white; 
                          border-radius: 6px; font-size: 10px; font-weight: 600; text-decoration: none;">
                  🔗 Accéder au site officiel →
                </a>
              ` : ''}
            </div>
          `;
        });
        
        popupContent += `</div>`;
      }
      
      // Footer
      popupContent += `
          <div style="font-size: 9px; color: #94a3b8; margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
            📍 ${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)} | Source: ${data.source || 'OpenAIP.net'}<br/>
            <span style="color: #f59e0b;">⚠️ ${data.disclaimer || 'Vérifiez les publications officielles avant le vol.'}</span>
          </div>
        </div>
      `;
      
      // Fermer le popup existant s'il y en a un
      if (airspacePopupRef.current) {
        mapInstance.current.closePopup(airspacePopupRef.current);
      }
      
      airspacePopupRef.current = L.popup({
        maxWidth: 480,
        className: 'openaip-info-popup'
      })
        .setLatLng(latlng)
        .setContent(popupContent)
        .openOn(mapInstance.current);
        
    } catch (error) {
      console.error('Error fetching OpenAIP airspace info:', error);
    }
  }, [showOpenAipLayer, isDrawingMode]);

  // Handler pour placer le point de décollage et calculer l'altitude max
  const handleTakeoffPointClick = useCallback(async (latlng) => {
    // Utiliser la ref pour vérifier le mode (plus fiable que l'état)
    if (!isTakeoffModeRef.current) return;
    
    setTakeoffLoading(true);
    setTakeoffPoint({ lat: latlng.lat, lng: latlng.lng });
    
    // Supprimer l'ancien marqueur s'il existe
    if (takeoffMarkerRef.current && mapInstance.current) {
      mapInstance.current.removeLayer(takeoffMarkerRef.current);
    }
    
    try {
      // Déterminer si on est en France (pour utiliser les données IGN/DGAC)
      const lat = latlng.lat;
      const lng = latlng.lng;
      const isInFrance = lat >= 41.3 && lat <= 51.1 && lng >= -5.1 && lng <= 9.6;
      
      // Récupérer les infos depuis DGAC (prioritaire pour France), OpenAIP ET IGN
      const [dgacResponse, openAipResponse, ignResponse] = await Promise.allSettled([
        isInFrance ? dgacApi.getAltitudeRestriction(latlng.lat, latlng.lng) : Promise.resolve({ data: { max_altitude_m: 120, restriction_type: 'default' } }),
        openAipApi.getInfo(latlng.lat, latlng.lng, 5000),
        isInFrance ? airspaceApi.getZonesAtPoint(latlng.lat, latlng.lng) : Promise.resolve({ data: { zones: [] } })
      ]);
      
      const dgacData = dgacResponse.status === 'fulfilled' ? dgacResponse.value.data : null;
      const openAipData = openAipResponse.status === 'fulfilled' ? openAipResponse.value.data : { features: [] };
      const ignData = ignResponse.status === 'fulfilled' ? ignResponse.value.data : { zones: [] };
      
      // Déterminer l'altitude max autorisée
      let maxAltitude = 120; // Altitude par défaut en catégorie Ouverte (120m AGL)
      let restrictions = [];
      let zoneType = 'open';
      let zoneName = 'Zone libre';
      let hasCTR = false;
      let hasProhibited = false;
      let hasUrbanRestriction = false;
      let dgacZoneColor = null;
      
      // 0. PRIORITÉ: Utiliser les données DGAC (zones colorées) pour la France
      if (isInFrance && dgacData && dgacData.restriction_type !== 'default' && dgacData.restriction_type !== 'error') {
        const dgacAltitude = dgacData.max_altitude_m;
        dgacZoneColor = dgacData.zone_color;
        
        if (dgacAltitude === 0) {
          // Zone rouge - Vol interdit
          hasProhibited = true;
          maxAltitude = 0;
          zoneType = 'prohibited';
          zoneName = 'Zone interdite DGAC';
          restrictions.push(`🔴 Vol INTERDIT sans autorisation (zone ${dgacZoneColor || 'rouge'})`);
          if (dgacData.remarque) {
            restrictions.push(`📋 ${dgacData.remarque}`);
          }
        } else if (dgacAltitude <= 100) {
          // Zone rose (30m), orange (50m) ou jaune (100m)
          maxAltitude = dgacAltitude;
          zoneType = `restricted_${dgacAltitude}`;
          zoneName = `Zone ${dgacZoneColor || 'restriction'} DGAC`;
          
          const colorEmoji = dgacAltitude <= 30 ? '🩷' : dgacAltitude <= 50 ? '🟠' : '🟡';
          restrictions.push(`${colorEmoji} Altitude max: ${dgacAltitude}m sans autorisation`);
          if (dgacData.remarque) {
            restrictions.push(`📋 ${dgacData.remarque}`);
          }
        }
      }
      
      // 1. Analyser les zones IGN/DGAC françaises (si pas déjà traité par l'API DGAC)
      // Skip si l'API DGAC a déjà fourni une restriction
      if (ignData.zones && ignData.zones.length > 0 && !dgacZoneColor) {
        for (const zone of ignData.zones) {
          const zType = zone.type?.toLowerCase() || '';
          const code = zone.code || zone.name || '';
          
          // Zones interdites (agglomérations, zones sensibles)
          if (zType.includes('interdit') || zType.includes('prohibited') || zone.flight_allowed === false) {
            hasProhibited = true;
            hasUrbanRestriction = true;
            maxAltitude = 0;
            zoneType = 'prohibited';
            zoneName = code;
            restrictions.push(`⛔ ${code}: Survol INTERDIT (zone peuplée/sensible)`);
          }
          // Zones avec hauteur max limitée
          else if (zone.max_height !== undefined && zone.max_height !== null) {
            const heightLimit = parseInt(zone.max_height);
            if (heightLimit < maxAltitude && !hasProhibited) {
              maxAltitude = heightLimit;
              restrictions.push(`🔴 ${code}: Hauteur max ${heightLimit}m`);
            }
          }
          // CTR depuis les données IGN
          else if (zType.includes('ctr')) {
            hasCTR = true;
            if (!hasProhibited) {
              maxAltitude = Math.min(maxAltitude, 50);
              zoneType = 'ctr';
              zoneName = code;
            }
            restrictions.push(`🔴 ${code}: CTR - Max 50m (autorisation requise au-delà)`);
          }
          // Zones à risque/dangereuses
          else if (zType.includes('danger') || zType.includes('restricted')) {
            restrictions.push(`🟠 ${code}: Zone à vérifier - Consulter NOTAM`);
          }
        }
      }
      
      // 2. Analyser les zones OpenAIP (pour compléter avec CTR/TMA européennes)
      if (openAipData.features && openAipData.features.length > 0) {
        // Analyser les zones pour trouver les restrictions
        for (const feature of openAipData.features) {
          const type = feature.type;
          const lowerLimit = feature.lower_limit || '';
          
          // Extraire la valeur numérique de la limite basse
          const lowerMatch = lowerLimit.match(/(\d+)/);
          const lowerValue = lowerMatch ? parseInt(lowerMatch[1]) : 0;
          const isFromGround = lowerLimit.includes('GND') || lowerLimit.includes('0ft') || lowerValue === 0;
          
          if (type === 'PROHIBITED') {
            // Zone P (Prohibited) = Vol totalement interdit
            if (isFromGround) {
              hasProhibited = true;
              maxAltitude = 0;
              zoneType = 'prohibited';
              zoneName = feature.name;
              restrictions.push(`⛔ ${feature.name}: Vol INTERDIT (zone P)`);
            }
          } else if (type === 'CTR') {
            // CTR = Zone de contrôle - Vol autorisé jusqu'à 50m sans autorisation
            if (isFromGround) {
              hasCTR = true;
              if (!hasProhibited) {
                maxAltitude = Math.min(maxAltitude, 50);
                zoneType = 'ctr';
                zoneName = feature.name;
              }
              restrictions.push(`🔴 ${feature.name}: Max 50m (autorisation requise au-delà)`);
            }
          } else if (type === 'RESTRICTED') {
            // Zone R (Restricted) = Restrictions temporaires, vérifier NOTAM
            // Ne bloque PAS le vol à 0m, mais avertit l'utilisateur
            restrictions.push(`🟠 ${feature.name}: Zone R - Vérifier activation par NOTAM`);
          } else if (type === 'DANGER') {
            // Zone D (Danger) = Activités dangereuses possibles
            restrictions.push(`⚠️ ${feature.name}: Zone D - Prudence recommandée`);
          } else if (type === 'TMA') {
            // TMA - généralement au-dessus de 120m mais à vérifier
            if (lowerValue < 400 && isFromGround) { // ~120m = 400ft
              const limitMeters = Math.floor(lowerValue * 0.3048);
              if (!hasProhibited && !hasCTR && limitMeters < maxAltitude) {
                maxAltitude = limitMeters;
              }
              restrictions.push(`🟡 ${feature.name}: Limite ${limitMeters}m`);
            }
          } else if (type === 'ATZ') {
            // ATZ = Zone de trafic d'aérodrome
            if (!hasProhibited && !hasCTR) {
              maxAltitude = Math.min(maxAltitude, 50);
              zoneType = 'atz';
              zoneName = feature.name;
            }
            restrictions.push(`🔵 ${feature.name}: Zone d'aérodrome - Max 50m`);
          }
        }
      }
      
      // Si aucune restriction majeure, zone libre à 120m
      if (restrictions.length === 0) {
        restrictions.push('✅ Zone libre - Catégorie Ouverte (max 120m)');
      }
      
      // Ajouter un avertissement sur les zones peuplées (non disponibles dans les données)
      const urbanWarning = '🏘️ Vérifiez le survol des zones peuplées sur Géoportail ou Mach7drone';
      
      setTakeoffAltitude({
        value: maxAltitude,
        type: zoneType,
        name: zoneName,
        restrictions: restrictions,
        urbanWarning: urbanWarning,
        country: openAipData.country_name || 'Europe'
      });
      
      // Déterminer le texte sous l'altitude
      let altitudeSubtext = 'AGL (au-dessus du sol)';
      if (maxAltitude === 0) {
        altitudeSubtext = 'Vol non autorisé dans cette zone (zone P)';
      } else if (zoneType === 'ctr' || zoneType === 'atz') {
        altitudeSubtext = 'Sans autorisation préalable';
      }
      
      // Créer le popup du marqueur
      const popupContent = `
        <div style="min-width: 250px; max-width: 320px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #e5e7eb;">
            <span style="font-size: 24px;">🛫</span>
            <div>
              <div style="font-weight: 700; color: #1e293b; font-size: 14px;">Point de décollage</div>
              <div style="font-size: 11px; color: #64748b;">${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}</div>
            </div>
          </div>
          
          <div style="background: ${maxAltitude === 0 ? '#fef2f2' : maxAltitude <= 50 ? '#fef3c7' : '#ecfdf5'}; 
                      padding: 12px; border-radius: 8px; margin-bottom: 10px;
                      border-left: 4px solid ${maxAltitude === 0 ? '#dc2626' : maxAltitude <= 50 ? '#f59e0b' : '#10b981'};">
            <div style="font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600;">Altitude max autorisée</div>
            <div style="font-size: 28px; font-weight: 800; color: ${maxAltitude === 0 ? '#dc2626' : maxAltitude <= 50 ? '#d97706' : '#059669'};">
              ${maxAltitude === 0 ? 'INTERDIT' : maxAltitude + 'm'}
            </div>
            <div style="font-size: 11px; color: #64748b;">
              ${altitudeSubtext}
            </div>
          </div>
          
          <div style="font-size: 11px; color: #374151;">
            <div style="font-weight: 600; margin-bottom: 6px;">📋 Zones détectées:</div>
            ${restrictions.map(r => `<div style="margin-bottom: 4px; padding-left: 8px;">${r}</div>`).join('')}
          </div>
          
          <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #94a3b8;">
            ⚠️ Information indicative. Vérifiez toujours les publications officielles (AIP, NOTAM) avant le vol.
          </div>
        </div>
      `;
      
      // Créer et ajouter le marqueur
      takeoffMarkerRef.current = L.marker([latlng.lat, latlng.lng], { icon: takeoffIcon })
        .bindPopup(popupContent, { maxWidth: 350 })
        .addTo(mapInstance.current)
        .openPopup();
      
      // Callback vers le parent
      if (onTakeoffPointSetRef.current) {
        onTakeoffPointSetRef.current({
          lat: latlng.lat,
          lng: latlng.lng,
          maxAltitude: maxAltitude,
          restrictions: restrictions
        });
      }
      
    } catch (error) {
      console.error('Error fetching altitude info:', error);
      // En cas d'erreur, utiliser les valeurs par défaut
      setTakeoffAltitude({
        value: 120,
        type: 'open',
        name: 'Zone libre',
        restrictions: ['✅ Zone libre - Catégorie Ouverte (par défaut)'],
        country: 'Europe'
      });
      
      const defaultPopup = `
        <div style="min-width: 220px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
            <span style="font-size: 24px;">🛫</span>
            <div style="font-weight: 700; color: #1e293b;">Point de décollage</div>
          </div>
          <div style="background: #ecfdf5; padding: 12px; border-radius: 8px; border-left: 4px solid #10b981;">
            <div style="font-size: 11px; color: #64748b;">Altitude max</div>
            <div style="font-size: 28px; font-weight: 800; color: #059669;">120m</div>
          </div>
          <div style="margin-top: 8px; font-size: 10px; color: #94a3b8;">
            ⚠️ Données non disponibles - Valeur par défaut
          </div>
        </div>
      `;
      
      takeoffMarkerRef.current = L.marker([latlng.lat, latlng.lng], { icon: takeoffIcon })
        .bindPopup(defaultPopup)
        .addTo(mapInstance.current)
        .openPopup();
    } finally {
      setTakeoffLoading(false);
      setIsTakeoffMode(false); // Désactiver le mode après placement
      isTakeoffModeRef.current = false; // Synchroniser la ref
    }
  }, []); // Pas de dépendances car on utilise des refs

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchAddress.length >= 3) {
        searchAddressAPI(searchAddress);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchAddress, searchAddressAPI]);

  // Keep callback refs updated
  useEffect(() => {
    onPolygonDrawnRef.current = onPolygonDrawn;
  }, [onPolygonDrawn]);

  useEffect(() => {
    onTakeoffPointSetRef.current = onTakeoffPointSet;
  }, [onTakeoffPointSet]);

  // Sync isTakeoffMode state with ref
  useEffect(() => {
    isTakeoffModeRef.current = isTakeoffMode;
  }, [isTakeoffMode]);

  // Load airspace zones
  const loadZones = useCallback(async () => {
    try {
      const response = await airspaceApi.getZones();
      setZones(response.data);
    } catch (error) {
      console.error('Failed to load zones:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load DJI Geozones
  const loadDjiZones = useCallback(async (center, radius) => {
    if (!showDjiLayer) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/api/dji-geozones?lat=${center.lat}&lng=${center.lng}&search_radius=${Math.min(radius, 100000)}&level=0,1,2,3,4,6,7&drone=mavic-3&country=FR`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.areas) {
          setDjiZones(data.areas);
        }
      }
    } catch (error) {
      console.error('Failed to load DJI zones:', error);
    }
  }, [showDjiLayer]);

  // Load Environmental Zones
  const loadEnvironmentalZones = useCallback(async (center, radius) => {
    // Check if any environmental layer is enabled
    const anyEnabled = Object.values(showEnvironmentalLayers).some(v => v);
    if (!anyEnabled) {
      setEnvironmentalZones([]);
      return;
    }
    
    setLoadingEnvZones(true);
    try {
      const response = await environmentalApi.getZones(
        center.lat, 
        center.lng, 
        Math.min(radius, 50000),
        'natura2000,rnc,pn,pnr'
      );
      
      if (response.data?.zones) {
        setEnvironmentalZones(response.data.zones);
      }
    } catch (error) {
      console.error('Failed to load environmental zones:', error);
    } finally {
      setLoadingEnvZones(false);
    }
  }, [showEnvironmentalLayers]);

  // Update DJI zones when map moves
  useEffect(() => {
    if (!mapInstance.current || !showDjiLayer) return;

    const updateDjiZones = () => {
      const map = mapInstance.current;
      if (!map) return;
      
      const center = map.getCenter();
      const bounds = map.getBounds();
      const radius = center.distanceTo(bounds.getNorthEast());
      
      // Only load if zoom level is sufficient (> 8)
      if (map.getZoom() > 8) {
        loadDjiZones(center, radius);
      } else {
        setDjiZones([]);
      }
    };

    mapInstance.current.on('moveend', updateDjiZones);
    
    // Initial load
    setTimeout(updateDjiZones, 1000);
    
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off('moveend', updateDjiZones);
      }
    };
  }, [showDjiLayer, loadDjiZones]);

  // Listen for German info click events (combined DFS + DIPUL)
  useEffect(() => {
    const handleGermanClick = (e) => {
      const { latlng, zoom } = e.detail;
      handleGermanInfoClick(latlng, zoom);
    };
    
    // Listen to both events but use the same handler
    window.addEventListener('dfs-airspace-click', handleGermanClick);
    window.addEventListener('dipul-zone-click', handleGermanClick);
    
    return () => {
      window.removeEventListener('dfs-airspace-click', handleGermanClick);
      window.removeEventListener('dipul-zone-click', handleGermanClick);
    };
  }, [handleGermanInfoClick]);

  // Listen for OpenAIP airspace clicks (European countries except Germany)
  useEffect(() => {
    const handleOpenAipClick = (e) => {
      const { latlng, zoom } = e.detail;
      handleOpenAipInfoClick(latlng, zoom);
    };
    
    window.addEventListener('openaip-airspace-click', handleOpenAipClick);
    
    return () => {
      window.removeEventListener('openaip-airspace-click', handleOpenAipClick);
    };
  }, [handleOpenAipInfoClick]);

  // Expose takeoff handler globally and cleanup
  useEffect(() => {
    window.__handleTakeoffClick = (latlng) => {
      handleTakeoffPointClick(latlng);
    };
    
    return () => {
      delete window.__handleTakeoffClick;
      delete window.__toggleTakeoffMode;
    };
  }, [handleTakeoffPointClick]);

  // Synchroniser l'état du bouton takeoff avec la classe CSS
  useEffect(() => {
    const btn = window.__takeoffButton;
    if (btn) {
      if (isTakeoffMode) {
        btn.classList.add('active');
        btn.style.backgroundColor = '#10b981';
        btn.style.color = 'white';
      } else {
        btn.classList.remove('active');
        btn.style.backgroundColor = '';
        btn.style.color = '#10b981';
      }
    }
  }, [isTakeoffMode]);

  // Toggle DIPUL layer visibility (Germany)
  useEffect(() => {
    if (!mapInstance.current || !dipulLayerRef.current) return;
    
    if (showDipulLayer) {
      if (!mapInstance.current.hasLayer(dipulLayerRef.current)) {
        dipulLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(dipulLayerRef.current)) {
        mapInstance.current.removeLayer(dipulLayerRef.current);
      }
    }
  }, [showDipulLayer]);

  // Toggle DFS INSPIRE Airspace layer visibility (Germany CTR/TMA)
  useEffect(() => {
    if (!mapInstance.current || !dfsAirspaceLayerRef.current) return;
    
    if (showDfsAirspaceLayer) {
      if (!mapInstance.current.hasLayer(dfsAirspaceLayerRef.current)) {
        dfsAirspaceLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(dfsAirspaceLayerRef.current)) {
        mapInstance.current.removeLayer(dfsAirspaceLayerRef.current);
      }
    }
  }, [showDfsAirspaceLayer]);

  // Toggle Belgian AOEM layer visibility
  useEffect(() => {
    if (!mapInstance.current || !belgiumLayerRef.current) return;
    
    if (showBelgiumLayer) {
      if (!mapInstance.current.hasLayer(belgiumLayerRef.current)) {
        belgiumLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(belgiumLayerRef.current)) {
        mapInstance.current.removeLayer(belgiumLayerRef.current);
      }
    }
  }, [showBelgiumLayer]);

  // Toggle Luxembourg DAC UAS layer visibility
  useEffect(() => {
    if (!mapInstance.current || !luxembourgLayerRef.current) return;

    if (showLuxembourgLayer) {
      if (!mapInstance.current.hasLayer(luxembourgLayerRef.current)) {
        luxembourgLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(luxembourgLayerRef.current)) {
        mapInstance.current.removeLayer(luxembourgLayerRef.current);
      }
    }
  }, [showLuxembourgLayer]);

  // Toggle Netherlands LVNL drone no-fly zones layer visibility
  useEffect(() => {
    if (!mapInstance.current || !netherlandsLayerRef.current) return;
    if (showNetherlandsLayer) {
      if (!mapInstance.current.hasLayer(netherlandsLayerRef.current)) {
        netherlandsLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(netherlandsLayerRef.current)) {
        mapInstance.current.removeLayer(netherlandsLayerRef.current);
      }
    }
  }, [showNetherlandsLayer]);

  // Toggle Switzerland BAZL UAS restrictions layer visibility
  // Also activates the ED-269 vector layer (1 239 BAZL zones in MongoDB)
  // so missions can be analysed against actual Swiss zone boundaries — the
  // WMTS tiles alone are only a visual overlay, never queried by Shapely.
  useEffect(() => {
    if (!mapInstance.current || !switzerlandLayerRef.current) return;
    if (showSwitzerlandLayer) {
      if (!mapInstance.current.hasLayer(switzerlandLayerRef.current)) {
        switzerlandLayerRef.current.addTo(mapInstance.current);
      }
      // Pair with the vector ED-269 layer so the user actually sees the
      // colour-coded restriction polygons (red AIR_TRAFFIC, orange
      // SENSITIVE, green NATURE) on top of the BAZL WMTS basemap.
      setEd269Country('ch');
      setShowEd269Layer(true);
      mapInstance.current.setView([46.8, 8.2], 7);
    } else {
      if (mapInstance.current.hasLayer(switzerlandLayerRef.current)) {
        mapInstance.current.removeLayer(switzerlandLayerRef.current);
      }
    }
  }, [showSwitzerlandLayer]);

  // Toggle Austria Austro Control airspace layer visibility
  // Also activates the ED-269 vector layer (51 ACG INSPIRE polygons)
  // so missions can actually be analysed against Austrian airspace.
  useEffect(() => {
    if (!mapInstance.current || !austriaLayerRef.current) return;
    if (showAustriaLayer) {
      if (!mapInstance.current.hasLayer(austriaLayerRef.current)) {
        austriaLayerRef.current.addTo(mapInstance.current);
      }
      setEd269Country('at');
      setShowEd269Layer(true);
      mapInstance.current.setView([47.5, 14.5], 7);
    } else {
      if (mapInstance.current.hasLayer(austriaLayerRef.current)) {
        mapInstance.current.removeLayer(austriaLayerRef.current);
      }
    }
  }, [showAustriaLayer]);

  // Toggle Iceland Samgöngustofa drone zones layer visibility
  useEffect(() => {
    if (!mapInstance.current || !icelandLayerRef.current) return;
    if (showIcelandLayer) {
      if (!mapInstance.current.hasLayer(icelandLayerRef.current)) {
        icelandLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(icelandLayerRef.current)) {
        mapInstance.current.removeLayer(icelandLayerRef.current);
      }
    }
  }, [showIcelandLayer]);

  // === ED-269 official UAS zones layer ===
  // Fetch the list of available countries on mount so the toggle knows which country to load
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/uas-zones/countries`)
      .then((r) => r.json())
      .then((d) => {
        setEd269Available(d.countries || []);
        // Pick the first available country as default if FR is not imported yet
        if (d.countries && d.countries.length && !d.countries.find((c) => c.country === 'FR')) {
          setEd269Country(d.countries[0].country.toLowerCase());
        }
      })
      .catch(() => {});
  }, []);

  // ED-269 official UAS zones — multi-country aware.
  //
  // The user can toggle several countries simultaneously (the layer panel
  // controls a Set of country codes via setEd269Country). We keep one
  // Leaflet GeoJSON layer per active country in `ed269LayersRef.current`
  // so individual countries can be added/removed without re-fetching the
  // others. When the global filter (`ed269Filter`) changes, every loaded
  // layer is invalidated and re-fetched with the new query parameters.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;

    const TYPE_STYLES = {
      PROHIBITED:        { color: '#dc2626', fillColor: '#dc2626', weight: 1.5, fillOpacity: 0.30 }, // red
      REQ_AUTHORISATION: { color: '#ea580c', fillColor: '#ea580c', weight: 1.5, fillOpacity: 0.25 }, // orange
      RESTRICTED:        { color: '#ea580c', fillColor: '#ea580c', weight: 1.5, fillOpacity: 0.25 },
      CONDITIONAL:       { color: '#eab308', fillColor: '#eab308', weight: 1.5, fillOpacity: 0.22 }, // amber
      NO_RESTRICTION:    { color: '#22c55e', fillColor: '#22c55e', weight: 1.0, fillOpacity: 0.10 }, // green
    };
    const DEFAULT_STYLE = { color: '#64748b', fillColor: '#94a3b8', weight: 1.0, fillOpacity: 0.15 };
    const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

    // Desired countries (lower-cased) currently visible on the map.
    const desired = new Set(
      showEd269Layer
        ? [...(ed269Country || [])].map((cc) => String(cc).toLowerCase())
        : []
    );
    const current = ed269LayersRef.current;

    // 1) Remove layers that are no longer desired OR whose filter is stale.
    for (const cc of Object.keys(current)) {
      const entry = current[cc];
      if (!desired.has(cc) || entry.filter !== ed269Filter) {
        if (entry.layer && map.hasLayer(entry.layer)) map.removeLayer(entry.layer);
        delete current[cc];
      }
    }

    // 2) Identify countries we still need to fetch.
    const toAdd = [...desired].filter((cc) => !current[cc]);
    if (toAdd.length === 0) {
      setEd269Loading(false);
      return undefined;
    }

    setEd269Loading(true);
    let cancelled = false;

    const buildLayer = (features) => L.geoJSON(features, {
      style: (feat) => TYPE_STYLES[feat.properties?.type] || DEFAULT_STYLE,
      onEachFeature: (feat, lyr) => {
        const p = feat.properties || {};
        const alt = p.altitude || {};
        const lower = (alt.lowerLimit !== undefined && alt.lowerLimit !== null) ? `${alt.lowerLimit}${alt.uom || 'm'} ${alt.lowerVerticalReference || ''}` : '—';
        const upper = (alt.upperLimit !== undefined && alt.upperLimit !== null) ? `${alt.upperLimit}${alt.uom || 'm'} ${alt.upperVerticalReference || ''}` : '—';
        const auth = p.authority?.name || (Array.isArray(p.authority) && p.authority[0]?.name) || '—';
        const reason = (p.reason || []).join(', ') || '—';
        const html = `
          <div style="max-width:300px;font-size:12px">
            <div style="font-weight:700;color:#0f172a;margin-bottom:4px">${escapeHtml(p.name || p.identifier || 'UAS Zone')}</div>
            <div style="color:#64748b;margin-bottom:6px">
              <span style="display:inline-block;background:${(TYPE_STYLES[p.type]||DEFAULT_STYLE).color};color:white;padding:1px 6px;border-radius:3px;font-weight:600">${escapeHtml(p.type)}</span>
              ${p.identifier ? `<span style="margin-left:6px">#${escapeHtml(p.identifier)}</span>` : ''}
            </div>
            ${p.message ? `<div style="margin-bottom:6px;color:#1e293b;line-height:1.4">${escapeHtml(p.message)}</div>` : ''}
            <div style="font-size:11px;color:#475569"><strong>Altitude</strong> : ${escapeHtml(lower)} → ${escapeHtml(upper)}</div>
            <div style="font-size:11px;color:#475569"><strong>Motif</strong> : ${escapeHtml(reason)}</div>
            <div style="font-size:11px;color:#475569"><strong>Autorité</strong> : ${escapeHtml(auth)}</div>
            <div style="font-size:10px;color:#94a3b8;margin-top:6px">Source : ED-269 ${escapeHtml(p.country_code || '')}</div>
          </div>`;
        lyr.bindPopup(html);
      },
    });

    // Fetch every missing country in parallel, then attach the layers in
    // one pass so the map only repaints once.
    Promise.all(
      toAdd.map((cc) => {
        // DIPUL (Germany) ships 88 599 zones — 56k of them are wind
        // turbines and industrial sites tagged INFRASTRUCTURES/risk 4.
        // Even after Douglas-Peucker simplification the full "important"
        // set weighs 15+ MB and freezes the canvas. We cap DE to the
        // 5 000 highest-risk zones so the map stays fluid; the backend
        // sorts by risk_level DESC so this keeps every AERONAUTICAL
        // zone (240) + the 4 760 most critical INFRASTRUCTURES.
        const perCountryLimit = cc === 'de' ? 5000 : 20000;
        const params = new URLSearchParams({ country: cc, limit: String(perCountryLimit) });
        if (ed269Filter === 'aero') params.set('categories', 'AERONAUTICAL');
        else if (ed269Filter === 'important') params.set('min_risk', '4');
        return fetch(`${API}/api/uas-zones?${params.toString()}`)
          .then((r) => r.json())
          .then((data) => ({ cc, features: (data.features || []).filter((f) => f.geometry) }))
          .catch((err) => {
            console.warn(`[ED-269] failed to load zones for ${cc}:`, err);
            return { cc, features: [] };
          });
      })
    )
      .then((results) => {
        if (cancelled || !mapInstance.current) return;
        for (const { cc, features } of results) {
          if (!features.length) continue;
          // The filter / desired set may have changed while the request was
          // in flight — only attach if we still want this country.
          if (!ed269LayersRef.current || (current !== ed269LayersRef.current)) return;
          if (current[cc]) continue; // someone else added it already
          const layer = buildLayer(features);
          layer.addTo(mapInstance.current);
          current[cc] = { layer, filter: ed269Filter };
        }
      })
      .finally(() => { if (!cancelled) setEd269Loading(false); });

    return () => { cancelled = true; };
  }, [showEd269Layer, ed269Country, ed269Filter]);


  // Toggle OpenAIP European airspace layer visibility
  useEffect(() => {
    if (!mapInstance.current || !openAipLayerRef.current) return;
    
    if (showOpenAipLayer) {
      if (!mapInstance.current.hasLayer(openAipLayerRef.current)) {
        openAipLayerRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(openAipLayerRef.current)) {
        mapInstance.current.removeLayer(openAipLayerRef.current);
      }
    }
  }, [showOpenAipLayer]);

  // Toggle Italy layer - uses OpenAIP and centers on Italy
  useEffect(() => {
    if (!mapInstance.current) return;
    
    if (showItalyLayer) {
      // Enable OpenAIP if not already (Italy uses OpenAIP data)
      if (openAipLayerRef.current && !mapInstance.current.hasLayer(openAipLayerRef.current)) {
        openAipLayerRef.current.addTo(mapInstance.current);
      }
      // Center on Italy
      mapInstance.current.setView([41.8719, 12.5674], 6);
    }
  }, [showItalyLayer]);

  // ───────────────────────────────────────────────────────────────────────
  // "Restriction drone" layer (Italy, OpenAIP). Renders only PROHIBITED /
  // RESTRICTED / DANGER / CTR polygons, colour-coded per the user spec.
  // Lazily fetched on first toggle and cached as a Leaflet GeoJSON layer
  // — toggling off detaches it; toggling on re-attaches (no re-fetch).
  // ───────────────────────────────────────────────────────────────────────
  useOpenAipRestrictionLayer({
    enabled: showItalyDroneRestrictions,
    mapInstance,
    layerRef: italyDroneRestrictionsLayerRef,
    country: 'IT',
    endpoint: '/api/openaip/italy/drone-restrictions',
    recenter: [41.9, 12.5, 6],
  });

  // Sweden — same layer mechanic, OpenAIP source while waiting for LFV
  // commercial licensing approval.
  useOpenAipRestrictionLayer({
    enabled: showSwedenDroneRestrictions,
    mapInstance,
    layerRef: swedenDroneRestrictionsLayerRef,
    country: 'SE',
    endpoint: '/api/openaip/SE/drone-restrictions',
    recenter: [62.5, 16.0, 5],
  });

  // Finland — Helsinki centre, country-wide zoom
  useOpenAipRestrictionLayer({
    enabled: showFinlandDroneRestrictions,
    mapInstance,
    layerRef: finlandDroneRestrictionsLayerRef,
    country: 'FI',
    endpoint: '/api/openaip/FI/drone-restrictions',
    recenter: [64.5, 26.0, 5],
  });

  // Belgium — Brussels-centred, country-wide zoom. OpenAIP vector overlay
  // sits on top of the NGI raster WMS (which has no clickable popups).
  useOpenAipRestrictionLayer({
    enabled: showBelgiumDroneRestrictions,
    mapInstance,
    layerRef: belgiumDroneRestrictionsLayerRef,
    country: 'BE',
    endpoint: '/api/openaip/BE/drone-restrictions',
    recenter: [50.5, 4.5, 8],
  });

  // Netherlands — Amsterdam-centred. 62 polygons (4 P / 23 R / 18 D / 17 CTR).
  useOpenAipRestrictionLayer({
    enabled: showNetherlandsDroneRestrictions,
    mapInstance,
    layerRef: netherlandsDroneRestrictionsLayerRef,
    country: 'NL',
    endpoint: '/api/openaip/NL/drone-restrictions',
    recenter: [52.2, 5.3, 8],
  });

  // Poland — Warsaw-centred. 119 polygons (32 P / 28 R / 28 D / 31 CTR).
  useOpenAipRestrictionLayer({
    enabled: showPolandDroneRestrictions,
    mapInstance,
    layerRef: polandDroneRestrictionsLayerRef,
    country: 'PL',
    endpoint: '/api/openaip/PL/drone-restrictions',
    recenter: [52.0, 19.5, 6],
  });

  // Norway — Oslo-centred, country-wide zoom (long thin shape — zoom 5).
  // 120 polygons (no PROHIBITED in OpenAIP — 25 R / 76 D / 19 CTR).
  useOpenAipRestrictionLayer({
    enabled: showNorwayDroneRestrictions,
    mapInstance,
    layerRef: norwayDroneRestrictionsLayerRef,
    country: 'NO',
    endpoint: '/api/openaip/NO/drone-restrictions',
    recenter: [64.0, 12.0, 5],
  });

  // Croatia / Greece / Bulgaria / Hungary / Slovenia — same pattern.
  useOpenAipRestrictionLayer({
    enabled: showCroatiaDroneRestrictions, mapInstance,
    layerRef: croatiaDroneRestrictionsLayerRef, country: 'HR',
    endpoint: '/api/openaip/HR/drone-restrictions',
    recenter: [45.1, 15.2, 7],
  });
  useOpenAipRestrictionLayer({
    enabled: showGreeceDroneRestrictions, mapInstance,
    layerRef: greeceDroneRestrictionsLayerRef, country: 'GR',
    endpoint: '/api/openaip/GR/drone-restrictions',
    recenter: [39.0, 22.5, 6],
  });
  useOpenAipRestrictionLayer({
    enabled: showBulgariaDroneRestrictions, mapInstance,
    layerRef: bulgariaDroneRestrictionsLayerRef, country: 'BG',
    endpoint: '/api/openaip/BG/drone-restrictions',
    recenter: [42.7, 25.5, 7],
  });
  useOpenAipRestrictionLayer({
    enabled: showHungaryDroneRestrictions, mapInstance,
    layerRef: hungaryDroneRestrictionsLayerRef, country: 'HU',
    endpoint: '/api/openaip/HU/drone-restrictions',
    recenter: [47.2, 19.5, 7],
  });
  useOpenAipRestrictionLayer({
    enabled: showSloveniaDroneRestrictions, mapInstance,
    layerRef: sloveniaDroneRestrictionsLayerRef, country: 'SI',
    endpoint: '/api/openaip/SI/drone-restrictions',
    recenter: [46.1, 14.8, 8],
  });

  // Toggle Spain layer - uses official ENAIRE WMS (ED-318) + OpenAIP
  useEffect(() => {
    if (!mapInstance.current) return;

    if (showSpainLayer) {
      // Add the ENAIRE official WMS layer (ED-318)
      if (spainEnaireLayerRef.current && !mapInstance.current.hasLayer(spainEnaireLayerRef.current)) {
        spainEnaireLayerRef.current.addTo(mapInstance.current);
      }
      // Also enable OpenAIP for general airspace context (CTR/TMA)
      if (openAipLayerRef.current && !mapInstance.current.hasLayer(openAipLayerRef.current)) {
        openAipLayerRef.current.addTo(mapInstance.current);
      }
      // Center on Spain (peninsula + Baleares)
      mapInstance.current.setView([40.4637, -3.7492], 6);
    } else {
      if (spainEnaireLayerRef.current && mapInstance.current.hasLayer(spainEnaireLayerRef.current)) {
        mapInstance.current.removeLayer(spainEnaireLayerRef.current);
      }
    }
  }, [showSpainLayer]);

  // Toggle Czechia layer — Czechia ED-318 data is stored in our MongoDB as
  // ED-269/ED-318 zones (6 431 polygons from AIM CR LKR310-320). There is no
  // separate WMS tile (AIM CR only publishes JSON), so turning the toggle on
  // simply selects Czechia in the ED-269 vector layer and recenters on Prague.
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showCzechiaLayer) {
      setEd269Country('cz');
      setShowEd269Layer(true);
      mapInstance.current.setView([49.8175, 15.4730], 7);
    }
    // We deliberately don't auto-disable ED-269 on un-toggle to let the user
    // keep the layer for other purposes.
  }, [showCzechiaLayer]);

  // Toggle Slovakia layer — same pattern as Czechia. The 273 LPS SR
  // airspace zones live in the same `uas_zones` collection, so flipping
  // the toggle activates the ED-269 vector layer with country=sk and
  // recenters the map on Bratislava (rough geographic centre of Slovakia
  // is around Banská Bystrica — we prefer the capital because that's
  // where the densest CTR is, giving the user immediate visual feedback).
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showSlovakiaLayer) {
      setEd269Country('sk');
      setShowEd269Layer(true);
      mapInstance.current.setView([48.7, 19.7], 7);
    }
  }, [showSlovakiaLayer]);

  // Toggle Denmark layer — Trafikstyrelsen publishes a static GeoJSON
  // (~1 826 zones: police, military, embassies, prisons, HEMS, civil
  // aerodromes, castles, …). Points come with a textual `Bufferzone`
  // which the backend ingestor converts into a circular polygon, so the
  // same ED-269 vector layer renders everything correctly. Toggling the
  // layer on selects `country=dk` and recenters on Copenhagen (densest
  // restrictions are around EKCH).
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showDenmarkLayer) {
      setEd269Country('dk');
      setShowEd269Layer(true);
      mapInstance.current.setView([55.85, 11.5], 7);
    }
  }, [showDenmarkLayer]);

  // Toggle Ireland layer — Irish Aviation Authority publishes a native
  // ED-269 GeoJSON (84 MultiPolygons covering controlled airspace around
  // EIDW/EICK, military and sensitive sites, model-aircraft clubs and
  // national parks). The layer recenters on Dublin (densest CTR).
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showIrelandLayer) {
      setEd269Country('ie');
      setShowEd269Layer(true);
      mapInstance.current.setView([53.35, -7.5], 7);
    }
  }, [showIrelandLayer]);

  // Toggle Estonia layer — Transpordiamet/EANS publishes a native ED-269
  // GeoJSON (~247 polygons covering CTR/TSA/RMZ, nature reserves and
  // sensitive sites). Recenter on Tallinn where the EETN CTR sits.
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showEstoniaLayer) {
      setEd269Country('ee');
      setShowEd269Layer(true);
      mapInstance.current.setView([58.8, 25.5], 7);
    }
  }, [showEstoniaLayer]);

  // Toggle Cyprus layer — DCA Cyprus publishes a pure ED-269 JSON
  // (141 zones, 62 polygons + 79 circles). Circles are expanded to
  // 32-vertex polygons in the backend ingestor. Recenter on Nicosia where
  // the LCNC CTR overlaps multiple sensitive zones (govt, comms).
  useEffect(() => {
    if (!mapInstance.current) return;
    if (showCyprusLayer) {
      setEd269Country('cy');
      setShowEd269Layer(true);
      mapInstance.current.setView([35.05, 33.3], 9);
    }
  }, [showCyprusLayer]);

  // DroneKeeper Hospitals layer toggle
  useEffect(() => {
    if (!mapInstance.current || !hospitalsLayerRef.current) return;
    
    if (showHospitalsLayer) {
      hospitalsLayerRef.current.addTo(mapInstance.current);
    } else {
      if (mapInstance.current.hasLayer(hospitalsLayerRef.current)) {
        mapInstance.current.removeLayer(hospitalsLayerRef.current);
      }
    }
  }, [showHospitalsLayer]);

  // DroneKeeper Airports layer toggle
  useEffect(() => {
    if (!mapInstance.current || !airportsLayerRef.current) return;
    
    if (showAirportsLayer) {
      airportsLayerRef.current.addTo(mapInstance.current);
    } else {
      if (mapInstance.current.hasLayer(airportsLayerRef.current)) {
        mapInstance.current.removeLayer(airportsLayerRef.current);
      }
    }
  }, [showAirportsLayer]);

  // Airport protection perimeters (AD_PPP) layer toggle
  useEffect(() => {
    if (!mapInstance.current || !airportsPPPLayerRef.current) return;
    
    if (showAirportsPPPLayer) {
      airportsPPPLayerRef.current.addTo(mapInstance.current);
    } else {
      if (mapInstance.current.hasLayer(airportsPPPLayerRef.current)) {
        mapInstance.current.removeLayer(airportsPPPLayerRef.current);
      }
    }
  }, [showAirportsPPPLayer]);

  // DroneKeeper Helistations layer toggle
  useEffect(() => {
    if (!mapInstance.current || !helistationsLayerRef.current) return;
    
    if (showHelistationsLayer) {
      helistationsLayerRef.current.addTo(mapInstance.current);
    } else {
      if (mapInstance.current.hasLayer(helistationsLayerRef.current)) {
        mapInstance.current.removeLayer(helistationsLayerRef.current);
      }
    }
  }, [showHelistationsLayer]);

  // DroneKeeper CTR layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkCtrLayerRef.current) return;
    if (showDkCtrLayer) {
      dkCtrLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkCtrLayerRef.current)) {
      mapInstance.current.removeLayer(dkCtrLayerRef.current);
    }
  }, [showDkCtrLayer]);

  // DroneKeeper TMA layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkTmaLayerRef.current) return;
    if (showDkTmaLayer) {
      dkTmaLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkTmaLayerRef.current)) {
      mapInstance.current.removeLayer(dkTmaLayerRef.current);
    }
  }, [showDkTmaLayer]);

  // DroneKeeper ATZ layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkAtzLayerRef.current) return;
    if (showDkAtzLayer) {
      dkAtzLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkAtzLayerRef.current)) {
      mapInstance.current.removeLayer(dkAtzLayerRef.current);
    }
  }, [showDkAtzLayer]);

  // DroneKeeper P (Prohibited) layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkPLayerRef.current) return;
    if (showDkPLayer) {
      dkPLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkPLayerRef.current)) {
      mapInstance.current.removeLayer(dkPLayerRef.current);
    }
  }, [showDkPLayer]);

  // DroneKeeper R (Restricted) layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkRLayerRef.current) return;
    if (showDkRLayer) {
      dkRLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkRLayerRef.current)) {
      mapInstance.current.removeLayer(dkRLayerRef.current);
    }
  }, [showDkRLayer]);

  // DroneKeeper D (Dangerous) layer toggle
  useEffect(() => {
    if (!mapInstance.current || !dkDLayerRef.current) return;
    if (showDkDLayer) {
      dkDLayerRef.current.addTo(mapInstance.current);
    } else if (mapInstance.current.hasLayer(dkDLayerRef.current)) {
      mapInstance.current.removeLayer(dkDLayerRef.current);
    }
  }, [showDkDLayer]);

  // EEA Natura 2000 WMS Layer (Europe-wide protected areas)
  useEffect(() => {
    if (!mapInstance.current) return;
    
    // Create Natura 2000 WMS layer if not exists
    if (!natura2000WmsRef.current) {
      natura2000WmsRef.current = L.tileLayer.wms(
        'https://bio.discomap.eea.europa.eu/arcgis/services/ProtectedSites/Natura2000Sites/MapServer/WMSServer',
        {
          layers: '0', // Layer 0 = Habitats and Birds Directive Sites (combined)
          format: 'image/png',
          transparent: true,
          version: '1.3.0',
          crs: L.CRS.EPSG3857,
          opacity: 0.5,
          attribution: '© Drone Maps · Espaces naturels'
        }
      );
    }
    
    if (showNatura2000Wms) {
      if (!mapInstance.current.hasLayer(natura2000WmsRef.current)) {
        natura2000WmsRef.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(natura2000WmsRef.current)) {
        mapInstance.current.removeLayer(natura2000WmsRef.current);
      }
    }
  }, [showNatura2000Wms]);

  // Update Environmental zones when map moves
  useEffect(() => {
    if (!mapInstance.current) return;
    
    // Check if any environmental layer is enabled
    const anyEnabled = Object.values(showEnvironmentalLayers).some(v => v);
    if (!anyEnabled) {
      if (zoneLayers.current.environmental) {
        mapInstance.current.removeLayer(zoneLayers.current.environmental);
        zoneLayers.current.environmental = null;
      }
      setEnvironmentalZones([]);
      return;
    }

    const updateEnvZones = () => {
      const map = mapInstance.current;
      if (!map) return;
      
      const center = map.getCenter();
      const bounds = map.getBounds();
      const radius = center.distanceTo(bounds.getNorthEast());
      
      // Only load if zoom level is sufficient (> 8)
      if (map.getZoom() > 8) {
        loadEnvironmentalZones(center, radius);
      } else {
        setEnvironmentalZones([]);
      }
    };

    mapInstance.current.on('moveend', updateEnvZones);
    
    // Initial load
    setTimeout(updateEnvZones, 1200);
    
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off('moveend', updateEnvZones);
      }
    };
  }, [showEnvironmentalLayers, loadEnvironmentalZones]);

  // Render Environmental zones on map
  useEffect(() => {
    if (!mapInstance.current) return;

    // Remove old layer
    if (zoneLayers.current.environmental) {
      mapInstance.current.removeLayer(zoneLayers.current.environmental);
      zoneLayers.current.environmental = null;
    }

    // Check if any environmental layer is enabled and we have zones
    const anyEnabled = Object.values(showEnvironmentalLayers).some(v => v);
    if (!anyEnabled || environmentalZones.length === 0) return;

    const envLayer = L.layerGroup();

    environmentalZones.forEach(zone => {
      // Skip if this type is disabled
      if (!showEnvironmentalLayers[zone.type]) return;
      
      // Get style for this zone type
      const style = ENV_ZONE_STYLES[zone.type] || ENV_ZONE_STYLES.natura2000_habitat;
      
      if (zone.geometry) {
        try {
          const geoJsonLayer = L.geoJSON(zone.geometry, {
            style: () => style
          });

          // Create popup content
          const popupContent = `
            <div style="min-width: 220px; max-width: 300px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: ${zone.color || style.color}; font-size: 14px;">
                ${zone.type === 'natura2000_habitat' ? '🌿' : 
                  zone.type === 'natura2000_oiseaux' ? '🐦' :
                  zone.type === 'reserve_naturelle' ? '🛡️' :
                  zone.type === 'parc_national' ? '🏔️' : '🌲'} ${zone.type_label || zone.type}
              </div>
              <div style="font-size: 13px; margin-bottom: 6px;">
                <strong>${zone.name}</strong>
              </div>
              <div style="font-size: 12px; color: #666;">
                ${zone.surface_ha ? `<div><strong>Surface:</strong> ${zone.surface_ha} ha</div>` : ''}
                ${zone.date_creation ? `<div><strong>Créé le:</strong> ${zone.date_creation}</div>` : ''}
                ${zone.description ? `<div><strong>Info:</strong> ${zone.description}</div>` : ''}
              </div>
              ${zone.contact ? `
                <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 12px;">
                  <strong>Contact:</strong><br/>
                  ${zone.contact.organisme || 'Non renseigné'}
                  ${zone.contact.telephone ? `<br/>📞 ${zone.contact.telephone}` : ''}
                  ${zone.contact.url ? `<br/><a href="${zone.contact.url}" target="_blank" style="color: #0ea5e9;">Plus d'infos →</a>` : ''}
                </div>
              ` : ''}
              <div style="margin-top: 8px; padding: 6px; background: #fef3c7; border-radius: 4px; font-size: 11px; color: #92400e;">
                ⚠️ Survol pouvant nécessiter une autorisation
              </div>
            </div>
          `;

          geoJsonLayer.bindPopup(popupContent);
          envLayer.addLayer(geoJsonLayer);
        } catch (error) {
          console.error('Error adding environmental zone:', zone.name, error);
        }
      }
    });

    envLayer.addTo(mapInstance.current);
    zoneLayers.current.environmental = envLayer;

  }, [environmentalZones, showEnvironmentalLayers]);

  // Render DJI zones on map
  useEffect(() => {
    if (!mapInstance.current || !showDjiLayer) {
      if (zoneLayers.current.dji) {
        mapInstance.current?.removeLayer(zoneLayers.current.dji);
        zoneLayers.current.dji = null;
      }
      return;
    }

    // Remove old layer
    if (zoneLayers.current.dji) {
      mapInstance.current.removeLayer(zoneLayers.current.dji);
    }

    if (djiZones.length === 0) return;

    // DJI Level colors
    const levelColors = {
      0: { color: '#fbbf24', fillColor: '#fbbf24', name: 'Warning' },      // Yellow
      1: { color: '#3b82f6', fillColor: '#3b82f6', name: 'Authorization' }, // Blue
      2: { color: '#ef4444', fillColor: '#ef4444', name: 'Restricted' },   // Red
      3: { color: '#f97316', fillColor: '#f97316', name: 'Enhanced Warning' }, // Orange
      4: { color: '#06b6d4', fillColor: '#06b6d4', name: 'Regulatory' },   // Cyan
      6: { color: '#6b7280', fillColor: '#6b7280', name: 'Altitude' },     // Gray
      7: { color: '#22c55e', fillColor: '#22c55e', name: 'Recommended' }   // Green
    };

    const djiLayer = L.layerGroup();

    djiZones.forEach(zone => {
      const level = zone.level || 2;
      const colors = levelColors[level] || levelColors[2];
      
      // Create circle for the zone
      if (zone.lat && zone.lng && zone.radius) {
        const circle = L.circle([zone.lat, zone.lng], {
          radius: zone.radius,
          color: colors.color,
          fillColor: colors.fillColor,
          fillOpacity: 0.25,
          weight: 2,
          dashArray: level === 0 || level === 3 ? '5, 5' : null
        });

        circle.bindPopup(`
          <div style="min-width: 200px;">
            <div style="font-weight: bold; margin-bottom: 5px; color: ${colors.color};">
              🚁 Zone DJI - ${colors.name}
            </div>
            <div style="font-size: 12px;">
              <strong>Nom:</strong> ${zone.name || 'N/A'}<br/>
              <strong>Type:</strong> ${zone.type === 0 ? 'Aéroport' : zone.type === 1 ? 'Zone spéciale' : 'Autre'}<br/>
              <strong>Altitude max:</strong> ${zone.height || 'N/A'}m<br/>
              ${zone.description ? `<strong>Description:</strong> ${zone.description}` : ''}
            </div>
          </div>
        `);

        djiLayer.addLayer(circle);
      }

      // Also render sub_areas if present
      if (zone.sub_areas) {
        zone.sub_areas.forEach(sub => {
          if (sub.lat && sub.lng && sub.radius) {
            const subCircle = L.circle([sub.lat, sub.lng], {
              radius: sub.radius,
              color: colors.color,
              fillColor: colors.fillColor,
              fillOpacity: 0.15,
              weight: 1,
              dashArray: '3, 3'
            });
            djiLayer.addLayer(subCircle);
          }
        });
      }
    });

    djiLayer.addTo(mapInstance.current);
    zoneLayers.current.dji = djiLayer;

  }, [djiZones, showDjiLayer]);

  // Handle map style change (standard/satellite)
  useEffect(() => {
    if (!mapInstance.current || !baseTileLayer.current) return;

    const map = mapInstance.current;
    
    // Remove current base layer
    map.removeLayer(baseTileLayer.current);

    // Add new base layer based on style
    if (mapStyle === 'satellite') {
      baseTileLayer.current = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: '&copy; <a href="https://www.esri.com/">Esri</a> - World Imagery',
          maxZoom: 19,
        }
      );
    } else {
      baseTileLayer.current = L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
          maxZoom: 19,
        }
      );
    }

    // Add at the bottom (below other layers)
    baseTileLayer.current.addTo(map);
    baseTileLayer.current.bringToBack();

  }, [mapStyle]);

  // Initialize map
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;

    // Allow user to navigate to French overseas territories via ?territory=X
    let initialCenter = [48.5, 6.0];
    let initialZoom = 5;

    // Pre-centre on the user's country when known (set at sign-up).
    // Skipped silently if the user picked "other" or for legacy accounts
    // without a country. The ?territory= query param keeps priority.
    const USER_COUNTRY_CENTERS = {
      fr: { c: [46.6, 2.5], z: 6 },
      de: { c: [51.0, 10.4], z: 6 },
      be: { c: [50.85, 4.35], z: 8 },
      lu: { c: [49.61, 6.13], z: 9 },
      nl: { c: [52.13, 5.29], z: 7 },
      ch: { c: [46.95, 7.45], z: 7 },
      at: { c: [47.59, 13.50], z: 7 },
      cz: { c: [49.82, 15.47], z: 7 },
      sk: { c: [48.70, 19.70], z: 7 },
      dk: { c: [55.85, 11.50], z: 7 },
      ie: { c: [53.35, -7.50], z: 7 },
      ee: { c: [58.80, 25.50], z: 7 },
      cy: { c: [35.05, 33.30], z: 9 },
      is: { c: [64.97, -18.55], z: 6 },
      es: { c: [40.42, -3.70], z: 6 },
      it: { c: [42.50, 12.50], z: 6 },
      pl: { c: [52.23, 21.01], z: 6 },
      pt: { c: [39.40, -8.20], z: 6 },
    };
    if (userCountry && USER_COUNTRY_CENTERS[userCountry]) {
      initialCenter = USER_COUNTRY_CENTERS[userCountry].c;
      initialZoom = USER_COUNTRY_CENTERS[userCountry].z;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const territoryKey = params.get('territory');
      if (territoryKey) {
        // Lazy import to avoid circular deps; fall back gracefully
        const TERRITORIES = {
          guadeloupe: { c: [16.25, -61.55], z: 10 },
          martinique: { c: [14.65, -61.02], z: 10 },
          guyane: { c: [3.94, -53.13], z: 7 },
          reunion: { c: [-21.12, 55.53], z: 10 },
          mayotte: { c: [-12.83, 45.16], z: 10 },
          'saint-pierre-miquelon': { c: [46.84, -56.32], z: 11 },
          'polynesie-francaise': { c: [-17.65, -149.42], z: 7 },
          'nouvelle-caledonie': { c: [-21.30, 165.50], z: 7 },
          'wallis-et-futuna': { c: [-13.30, -176.20], z: 10 },
          taaf: { c: [-49.35, 70.22], z: 5 },
          // Aliases for European countries (future use)
          metropole: { c: [46.6, 2.5], z: 6 },
        };
        const t = TERRITORIES[territoryKey];
        if (t) {
          initialCenter = t.c;
          initialZoom = t.z;
        }
      }
    } catch (e) {
      // Defensive: keep default Europe view
    }

    // Initialize map - default European view, or DOM-TOM if ?territory=X is set
    // preferCanvas: true is the single biggest performance win for dense
    // vector datasets like ED-269 (Spain ships 17k polygons, France 7k).
    // SVG renders each polygon as a separate DOM node and starts choking
    // around 2-3k. Canvas keeps everything on a single bitmap and stays
    // smooth even at 20k features.
    const map = L.map(mapRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: true,
      minZoom: 3,
      preferCanvas: true,
    });
    mapInstance.current = map;

    // Product analytics: one `map_zone_clicked` per popup opened. Leaflet
    // emits `popupopen` for every popup regardless of source (ED-269 zone,
    // DFS airspace, DIPUL restriction, ENAIRE polygon…) so this catches
    // all "user explored a zone" gestures without touching each handler.
    // We debounce lightly via a WeakSet so the same popup isn't counted
    // twice if the user closes and reopens quickly.
    let lastPopupTs = 0;
    map.on('popupopen', () => {
      const now = Date.now();
      if (now - lastPopupTs < 300) return; // dedupe rapid double-fires
      lastPopupTs = now;
      if (!compactMode) {
        trackEvent('map_zone_clicked', {
          zoom: map.getZoom(),
        });
      }
    });

    // Add base tile layer (standard view)
    baseTileLayer.current = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    // Add DGAC/IGN WMTS Layer - Restrictions UAS catégorie Ouverte (same as cartes.gouv.fr)
    // Source: Geoplateforme (data.geopf.fr)
    ignWmsLayer.current = L.tileLayer(
      'https://data.geopf.fr/wmts?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&LAYER=' + DGAC_DRONE_LAYER + '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
      {
        minZoom: 6,
        maxZoom: 18,
        opacity: 0.85,
        attribution: '© Drone Maps · Données officielles'
      }
    );
    ignWmsLayer.current.addTo(map);

    // Add DIPUL WMS Layer - German drone restrictions
    // Source: Deutsche Flugsicherung (DFS) - dipul.de
    dipulLayerRef.current = L.tileLayer.wms(DIPUL_WMS_URL, {
      layers: DIPUL_LAYERS.join(','),
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 6,
      maxZoom: 18,
      opacity: 0.75,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add DFS INSPIRE WMS Layer - German airspace (CTR, TMA, etc.)
    // Source: DFS Deutsche Flugsicherung - INSPIRE Air Transport Network
    dfsAirspaceLayerRef.current = L.tileLayer.wms(DFS_INSPIRE_WMS_URL, {
      layers: DFS_AIRSPACE_LAYER,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 5,
      maxZoom: 18,
      opacity: 0.6,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Belgian AOEM WMS Layer - Aeronautical Obstacle Evaluation Map
    // Source: NGI Belgium - DGTA/DGLV (Belgian Civil Aviation Authority)
    belgiumLayerRef.current = L.tileLayer.wms(BELGIUM_WMS_URL, {
      layers: BELGIUM_AOEM_LAYER,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 6,
      maxZoom: 18,
      opacity: 0.7,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Luxembourg DAC UAS Geographical Zones WMS Layer
    // Source: Géoportail Luxembourg (DAC) - layers 1927 (0-50m AGL) + 1928 (50-120m AGL)
    // Eurocae ED-269 compliant - legally binding zones for drone flights
    luxembourgLayerRef.current = L.tileLayer.wms(LUXEMBOURG_WMS_URL, {
      layers: LUXEMBOURG_UAS_LAYERS,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 6,
      maxZoom: 18,
      opacity: 0.7,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Netherlands LVNL Drone No-Fly Zones WMS Layer
    // Source: PDOK / Luchtverkeersleiding Nederland (LVNL)
    // Layers: luchtvaartgebieden (zones aéro) + landingsite (sites d'atterrissage)
    netherlandsLayerRef.current = L.tileLayer.wms(NETHERLANDS_WMS_URL, {
      layers: NETHERLANDS_UAS_LAYERS,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 5,
      maxZoom: 18,
      opacity: 0.7,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Switzerland FOCA/BAZL UAS Restrictions WMTS Tile Layer
    // Source: map.geo.admin.ch - Office Fédéral de l'Aviation Civile (BAZL)
    // Couche officielle ch.bazl.einschraenkungen-drohnen
    switzerlandLayerRef.current = L.tileLayer(SWITZERLAND_TILES_URL, {
      minZoom: 5,
      maxZoom: 18,
      opacity: 0.75,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Austria Austro Control INSPIRE Airspace WMS Layer
    // Source: sdigeo-free.austrocontrol.at - INSPIRE Transport Network Air
    // Couche AirspaceArea : espace aérien autrichien (CTR, TMA, R, P, D)
    austriaLayerRef.current = L.tileLayer.wms(AUSTRIA_WMS_URL, {
      layers: AUSTRIA_AIRSPACE_LAYER,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 5,
      maxZoom: 16,
      opacity: 0.7,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add Spain ENAIRE UAS Geographical Zones WMS Layer (ED-318)
    // Source: servais.enaire.es — Servicio Cartográfico ENAIRE (ais@enaire.es)
    // Layers WMS : "0" = Infraestructuras, "2" = Aero (AIRAC), "3" = Urbano
    // Standard EUROCAE ED-318 (UAS zones, remplace ED-269 ZUAS)
    spainEnaireLayerRef.current = L.tileLayer.wms(SPAIN_ENAIRE_WMS_URL, {
      layers: SPAIN_ENAIRE_LAYERS,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 5,
      maxZoom: 18,
      opacity: 0.7,
      attribution: '© Drone Maps · Données officielles'
    });
    // In demoMode, showSpainLayer is initially `true` and its toggle effect already
    // ran before this init effect, so we need to add the layer here on first mount.
    if (showSpainLayer) {
      spainEnaireLayerRef.current.addTo(mapInstance.current);
    }

    // Add Iceland Samgöngustofa drone zones WMS Layer
    // Source: gis.natt.is/geoserver — Samgöngustofa (Iceland Transport Authority)
    // Couches : dronemap_ice_geom (carte zones drone) + skyndilokanir_dronar (fermetures temporaires)
    // Licence : Opin gögn LMÍ (Open Data) — usage commercial autorisé avec attribution
    icelandLayerRef.current = L.tileLayer.wms(ICELAND_WMS_URL, {
      layers: ICELAND_DRONE_LAYERS,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      minZoom: 4,
      maxZoom: 16,
      opacity: 0.75,
      attribution: '© Drone Maps · Données officielles'
    });
    // Ne pas ajouter par défaut - désactivé

    // Add OpenAIP European Airspace Tile Layer (CTR, TMA, P/D/R zones)
    // Source: OpenAIP.net - Comprehensive aeronautical data
    openAipLayerRef.current = L.tileLayer(OPENAIP_TILES_URL, {
      subdomains: ['a', 'b', 'c'],
      minZoom: 4,
      maxZoom: 14,
      opacity: 0.85,
      attribution: '© Drone Maps · Espaces aériens européens'
    });
    // Attach immediately if the initial state says so (landing demo,
    // authenticated user with a country). The dedicated toggle effect
    // below can't run before the map is ready, so we short-circuit here
    // to avoid a "no European airspace visible" bug on first mount.
    if (showOpenAipLayer) {
      openAipLayerRef.current.addTo(map);
    }

    // DroneKeeper Vector Tile Layers (Hospitals, Airports, Helistations)
    // Source: DroneKeeper/Tegola - POI data for drone operations
    const vectorTileOptions = {
      interactive: true,
      maxNativeZoom: 18,
      minZoom: 5,
      attribution: ''
    };

    // -------------------------------------------------------------------
    // European POI factory (hospitals, helipads, …)
    //
    // Returns a L.layerGroup that refetches /api/poi by viewport bbox on
    // every Leaflet `moveend`. We deliberately avoid Leaflet.markercluster
    // for now — at zoom ≥ 6 the per-bbox cap of 2000 features keeps the
    // canvas perfectly fluid, and the server-side cap means we never push
    // the full 87 000 POIs to the browser even when zoomed all the way out.
    //
    // Tied to `map` via closure: the layer is created once and survives
    // re-renders thanks to the surrounding useEffect ref pattern.
    // -------------------------------------------------------------------
    const createEuropeanPoiLayer = ({ map: mapRef, category, emoji, defaultLabel, fill, stroke, helpText }) => {
      const layer = L.layerGroup();
      let inflight = null; // current AbortController
      let timer = null;    // debounce

      const renderFeatures = (features) => {
        layer.clearLayers();
        const zoom = mapRef.getZoom();
        const radius = Math.max(3, Math.min(7, zoom - 5));
        features.forEach((f) => {
          const [lng, lat] = f.geometry?.coordinates || [];
          if (lat == null || lng == null) return;
          const marker = L.circleMarker([lat, lng], {
            radius,
            fillColor: fill,
            color: stroke,
            weight: 1.5,
            opacity: 1,
            fillOpacity: 0.85,
          });
          const props = f.properties || {};
          const name = props.name || defaultLabel;
          const country = props.country ? ` <span style="font-size:10px;color:#94a3b8">${props.country}</span>` : '';
          marker.bindPopup(
            `<div style="padding:6px 8px;min-width:160px"><strong>${emoji} ${name}</strong>${country}<br/><span style="font-size:11px;color:#dc2626">${helpText}</span></div>`,
            { closeButton: true }
          );
          layer.addLayer(marker);
        });
      };

      const fetchPois = () => {
        // Only fetch when the layer is actually on the map — otherwise we
        // waste bandwidth on a panel the user can't see.
        if (!mapRef.hasLayer(layer)) return;
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const b = mapRef.getBounds();
        const bbox = `${b.getSouth().toFixed(4)},${b.getWest().toFixed(4)},${b.getNorth().toFixed(4)},${b.getEast().toFixed(4)}`;
        // Cancel any prior in-flight request so fast pans don't stack.
        if (inflight) inflight.abort();
        inflight = new AbortController();
        fetch(`${apiUrl}/api/poi?category=${category}&bbox=${bbox}&limit=2000`, { signal: inflight.signal })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
          .then((data) => renderFeatures(data.features || []))
          .catch((e) => {
            if (e.name !== 'AbortError') console.warn(`POI ${category} fetch failed:`, e);
          });
      };

      const debouncedFetch = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(fetchPois, 250);
      };

      // Wire moveend + zoomend + initial activation. We listen on `add`
      // so the first fetch fires the moment the user enables the toggle.
      layer.on('add', () => {
        // Re-render immediately so a stale set isn't shown on toggle.
        layer.clearLayers();
        fetchPois();
      });
      mapRef.on('moveend zoomend', debouncedFetch);

      return layer;
    };

    // Hospitals layer — European OSM data (75k+ points covering 13 countries)
    // served by GET /api/poi?category=hospital. We fetch by viewport on every
    // moveend so we never push more than a few thousand markers to the DOM.
    try {
      hospitalsLayerRef.current = createEuropeanPoiLayer({
        map,
        category: 'hospital',
        emoji: '🏥',
        defaultLabel: 'Hôpital',
        fill: '#ef4444',
        stroke: '#b91c1c',
        helpText: 'Zone sensible — Hélisurface possible',
      });
    } catch (e) {
      console.warn('Failed to create hospitals layer:', e);
    }

    // Airports location layer (blue markers)
    try {
      airportsLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/AD_LOCATION/{z}/{x}/{y}.pbf`,
        {
          ...vectorTileOptions,
          vectorTileLayerStyles: {
            AD_LOCATION: (properties, zoom) => ({
              radius: Math.max(5, zoom - 3),
              fillColor: '#3b82f6',
              color: '#1d4ed8',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.8,
              fill: true
            })
          },
          getFeatureId: (f) => f.properties?.id || f.properties?.icao_code || Math.random()
        }
      );
      airportsLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'Aéroport';
          const icao = props.icao_code || props.icao || '';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>✈️ ${name}</strong>${icao ? `<br/><span style="font-size:11px;font-family:monospace">${icao}</span>` : ''}<br/><span style="font-size:11px;color:#dc2626">Zone de restriction</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create airports layer:', e);
    }

    // Airport protection perimeters (AD_PPP) - light blue zones
    try {
      airportsPPPLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/AD_PPP/{z}/{x}/{y}.pbf`,
        {
          ...vectorTileOptions,
          vectorTileLayerStyles: {
            AD_PPP: {
              fillColor: '#60a5fa',
              color: '#2563eb',
              weight: 1.5,
              opacity: 0.8,
              fillOpacity: 0.2,
              fill: true
            }
          },
          getFeatureId: (f) => f.properties?.id || Math.random()
        }
      );
      airportsPPPLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'Périmètre aéroport';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>✈️ ${name}</strong><br/><span style="font-size:11px;color:#2563eb">Périmètre de protection aéroport</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create AD_PPP layer:', e);
    }

    // Helistations / heliports — European OSM data via /api/poi?category=helipad
    // (~12k points). Same per-bbox fetcher as hospitals.
    try {
      helistationsLayerRef.current = createEuropeanPoiLayer({
        map,
        category: 'helipad',
        emoji: '🚁',
        defaultLabel: 'Hélistation',
        fill: '#f97316',
        stroke: '#c2410c',
        helpText: 'Zone sensible — Trafic hélicoptère',
      });
    } catch (e) {
      console.warn('Failed to create helistations layer:', e);
    }

    // DroneKeeper Airspace Layers (CTR, TMA, ATZ, P, R, D)
    const airspaceVectorOptions = {
      interactive: true,
      maxNativeZoom: 18,
      minZoom: 4,
      attribution: ''
    };

    // CTR - Control Zones (cyan/teal)
    try {
      dkCtrLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/CTR/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            CTR: {
              fillColor: '#06b6d4',
              color: '#0891b2',
              weight: 2,
              opacity: 0.9,
              fillOpacity: 0.25,
              fill: true
            }
          }
        }
      );
      dkCtrLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'CTR';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🔵 CTR - ${name}</strong><br/><span style="font-size:11px;color:#0891b2">Zone de contrôle</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create CTR layer:', e);
    }

    // TMA - Terminal Areas (purple)
    try {
      dkTmaLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/TMA/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            TMA: {
              fillColor: '#8b5cf6',
              color: '#7c3aed',
              weight: 2,
              opacity: 0.9,
              fillOpacity: 0.2,
              fill: true
            }
          }
        }
      );
      dkTmaLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'TMA';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🟣 TMA - ${name}</strong><br/><span style="font-size:11px;color:#7c3aed">Zone terminale</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create TMA layer:', e);
    }

    // ATZ - Aerodrome Traffic Zones (blue)
    try {
      dkAtzLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/ATZ/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            ATZ: {
              fillColor: '#3b82f6',
              color: '#2563eb',
              weight: 2,
              opacity: 0.9,
              fillOpacity: 0.2,
              fill: true
            }
          }
        }
      );
      dkAtzLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'ATZ';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🔷 ATZ - ${name}</strong><br/><span style="font-size:11px;color:#2563eb">Zone de trafic aérodrome</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create ATZ layer:', e);
    }

    // P - Prohibited Zones (red)
    try {
      dkPLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/P/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            P: {
              fillColor: '#ef4444',
              color: '#dc2626',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.35,
              fill: true
            }
          }
        }
      );
      dkPLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'Zone P';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🔴 ${name}</strong><br/><span style="font-size:11px;color:#dc2626">Zone INTERDITE</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create P layer:', e);
    }

    // R - Restricted Zones (orange)
    try {
      dkRLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/R/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            R: {
              fillColor: '#f97316',
              color: '#ea580c',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.3,
              fill: true
            }
          }
        }
      );
      dkRLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'Zone R';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🟠 ${name}</strong><br/><span style="font-size:11px;color:#ea580c">Zone RESTREINTE</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create R layer:', e);
    }

    // D - Dangerous Zones (yellow)
    try {
      dkDLayerRef.current = L.vectorGrid.protobuf(
        `${VECTOR_TILES_URL}/D/{z}/{x}/{y}.pbf`,
        {
          ...airspaceVectorOptions,
          vectorTileLayerStyles: {
            D: {
              fillColor: '#eab308',
              color: '#ca8a04',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.25,
              fill: true
            }
          }
        }
      );
      dkDLayerRef.current.on('click', (e) => {
        if (e.layer?.properties) {
          const props = e.layer.properties;
          const name = props.name || props.nom || 'Zone D';
          L.popup()
            .setLatLng(e.latlng)
            .setContent(`<div style="padding:8px"><strong>🟡 ${name}</strong><br/><span style="font-size:11px;color:#ca8a04">Zone DANGEREUSE</span></div>`)
            .openOn(map);
        }
      });
    } catch (e) {
      console.warn('Failed to create D layer:', e);
    }

    // Initialize feature groups
    drawnItems.current = new L.FeatureGroup();
    map.addLayer(drawnItems.current);

    zonesLayer.current = new L.FeatureGroup();
    map.addLayer(zonesLayer.current);

    // Track zoom level changes
    map.on('zoomend', () => {
      setCurrentZoom(map.getZoom());
    });
    setCurrentZoom(map.getZoom());

    // Track map center for the NOTAM overlay. Debounced by piggybacking
    // on Leaflet's own `moveend` (fires ~150ms after the user releases
    // the mouse, once) so we don't hammer the NOTAM API while panning.
    // Seed with the initial center so the first render has something to
    // fetch instead of waiting for the first pan.
    const notamSeed = map.getCenter();
    setMapCenterForNotams({ lat: notamSeed.lat, lng: notamSeed.lng });
    map.on('moveend', () => {
      const c = map.getCenter();
      setMapCenterForNotams({ lat: c.lat, lng: c.lng });
    });

    // Store a reference to handle takeoff clicks - use preclick for priority over layer clicks
    map.on('preclick', (e) => {
      // Si mode takeoff actif, placer le point de décollage
      if (isTakeoffModeRef.current) {
        // Appeler directement le handler
        if (window.__handleTakeoffClick) {
          window.__handleTakeoffClick(e.latlng);
        }
      }
    });
    
    // Handler for info popups (separate from takeoff)
    map.on('click', async (e) => {
      // Ne pas traiter si mode takeoff actif (déjà géré par preclick)
      if (isTakeoffModeRef.current) {
        return;
      }
      
      const zoom = map.getZoom();
      // Ne déclencher que si zoom suffisant pour les popups d'info
      if (zoom >= 7) {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        
        // Bbox de l'Allemagne approximative
        const isInGermany = lat >= 47.3 && lat <= 55.0 && lng >= 5.9 && lng <= 15.0;
        
        if (isInGermany) {
          // Déclencher l'événement combiné pour les infos allemandes
          const germanInfoEvent = new CustomEvent('dfs-airspace-click', { 
            detail: { latlng: e.latlng, zoom } 
          });
          window.dispatchEvent(germanInfoEvent);
        } else {
          // Pour les autres pays européens, déclencher l'événement OpenAIP
          const openAipEvent = new CustomEvent('openaip-airspace-click', { 
            detail: { latlng: e.latlng, zoom } 
          });
          window.dispatchEvent(openAipEvent);
        }
      }
    });

    // Add draw controls if editable
    if (editable) {
      const drawControl = new L.Control.Draw({
        position: 'topright',
        draw: {
          polyline: false,
          rectangle: {
            shapeOptions: {
              color: '#0ea5e9',
              fillColor: '#0ea5e9',
              fillOpacity: 0.3,
              weight: 3,
            },
          },
          circle: false,
          circlemarker: false,
          marker: false,
          polygon: {
            allowIntersection: false,
            shapeOptions: {
              color: '#0ea5e9',
              fillColor: '#0ea5e9',
              fillOpacity: 0.3,
              weight: 3,
            },
          },
        },
        edit: {
          featureGroup: drawnItems.current,
          remove: true,
          edit: true,
        },
      });
      drawControlRef.current = drawControl;
      map.addControl(drawControl);

      // Injecter le bouton de décollage dans la barre d'outils de dessin Leaflet
      // On attend que le DOM soit prêt
      setTimeout(() => {
        const drawToolbar = document.querySelector('.leaflet-draw-toolbar');
        // Vérifier si le bouton n'existe pas déjà
        if (drawToolbar && !drawToolbar.querySelector('.leaflet-draw-draw-takeoff')) {
          // Créer le bouton de décollage
          const takeoffButton = document.createElement('a');
          takeoffButton.className = 'leaflet-draw-draw-marker leaflet-draw-draw-takeoff';
          takeoffButton.href = '#';
          takeoffButton.title = 'Point de décollage - Cliquez sur la carte pour placer';
          takeoffButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          `;
          takeoffButton.style.cssText = 'display: flex; align-items: center; justify-content: center;';
          
          // Ajouter le bouton à la barre de dessin
          drawToolbar.appendChild(takeoffButton);
          
          // Stocker la référence au bouton
          window.__takeoffButton = takeoffButton;
          
          // Gérer le clic
          L.DomEvent.on(takeoffButton, 'click', function(e) {
            L.DomEvent.stop(e);
            if (window.__toggleTakeoffMode) {
              window.__toggleTakeoffMode();
            }
          });
        }
      }, 100);
      
      // Exposer la fonction de toggle pour le contrôle
      window.__toggleTakeoffMode = () => {
        setIsTakeoffMode(prev => !prev);
        map.closePopup();
      };

      // Handle DRAWSTART event - disable popups during drawing
      map.on(L.Draw.Event.DRAWSTART, () => {
        isDrawingRef.current = true;
        setIsDrawingMode(true);
        // Close any open popup when starting to draw
        map.closePopup();
      });

      // Handle DRAWSTOP event - re-enable popups after drawing
      map.on(L.Draw.Event.DRAWSTOP, () => {
        isDrawingRef.current = false;
        setIsDrawingMode(false);
      });

      // Handle EDITSTART event - disable popups during editing
      map.on(L.Draw.Event.EDITSTART, () => {
        isDrawingRef.current = true;
        setIsDrawingMode(true);
        map.closePopup();
      });

      // Handle EDITSTOP event - re-enable popups after editing
      map.on(L.Draw.Event.EDITSTOP, () => {
        isDrawingRef.current = false;
        setIsDrawingMode(false);
      });

      // Handle DELETESTART event - disable popups during deletion
      map.on(L.Draw.Event.DELETESTART, () => {
        isDrawingRef.current = true;
        setIsDrawingMode(true);
        map.closePopup();
      });

      // Handle DELETESTOP event - re-enable popups after deletion
      map.on(L.Draw.Event.DELETESTOP, () => {
        isDrawingRef.current = false;
        setIsDrawingMode(false);
      });

      // Intercept popupopen event - close popup immediately if in drawing or takeoff mode
      map.on('popupopen', (e) => {
        if (isDrawingRef.current || isTakeoffModeRef.current) {
          // Check if this is not the takeoff marker popup
          const popup = e.popup;
          const content = popup.getContent() || '';
          const isTakeoffPopup = typeof content === 'string' && content.includes('Point de décollage');
          
          if (!isTakeoffPopup) {
            // Small delay to ensure the popup is fully opened before closing
            setTimeout(() => {
              map.closePopup();
            }, 10);
          }
        }
      });

      // Handle CREATED event
      map.on(L.Draw.Event.CREATED, (e) => {
        try {
          drawnItems.current.clearLayers();
          const layer = e.layer;
          drawnItems.current.addLayer(layer);
          
          const geoJson = layer.toGeoJSON();
          if (onPolygonDrawnRef.current) {
            onPolygonDrawnRef.current(geoJson.geometry);
          }
        } catch (error) {
          console.error('Error handling draw create:', error);
        }
      });

      // Handle EDITED event - fixed to properly handle edited layers
      map.on(L.Draw.Event.EDITED, (e) => {
        try {
          const layers = e.layers;
          let lastGeometry = null;
          
          layers.eachLayer((layer) => {
            try {
              const geoJson = layer.toGeoJSON();
              lastGeometry = geoJson.geometry;
            } catch (err) {
              console.error('Error converting layer to GeoJSON:', err);
            }
          });
          
          if (lastGeometry && onPolygonDrawnRef.current) {
            onPolygonDrawnRef.current(lastGeometry);
          }
        } catch (error) {
          console.error('Error handling draw edit:', error);
        }
      });

      // Handle DELETED event
      map.on(L.Draw.Event.DELETED, (e) => {
        try {
          // Check if there are any remaining layers
          const remainingLayers = drawnItems.current.getLayers();
          if (remainingLayers.length === 0) {
            if (onPolygonDrawnRef.current) {
              onPolygonDrawnRef.current(null);
            }
          } else {
            // If there are remaining layers, send the last one
            const lastLayer = remainingLayers[remainingLayers.length - 1];
            const geoJson = lastLayer.toGeoJSON();
            if (onPolygonDrawnRef.current) {
              onPolygonDrawnRef.current(geoJson.geometry);
            }
          }
        } catch (error) {
          console.error('Error handling draw delete:', error);
          if (onPolygonDrawnRef.current) {
            onPolygonDrawnRef.current(null);
          }
        }
      });

      // Handle DRAWSTART - clear existing when starting new draw
      map.on(L.Draw.Event.DRAWSTART, () => {
        drawnItems.current.clearLayers();
      });
    }

    // Load zones
    loadZones();

    // Expose map instance via callback
    if (onMapReady) {
      onMapReady(map);
    }

    // Cleanup
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off();
        mapInstance.current.remove();
        mapInstance.current = null;
        drawnItems.current = null;
        zonesLayer.current = null;
        ignWmsLayer.current = null;
        searchMarkerRef.current = null;
        drawControlRef.current = null;
        zoneLayers.current = { ctr: null, tma: null, restricted: null, prohibited: null, danger: null, dji: null, environmental: null };
      }
    };
  }, [editable, loadZones]);

  // Render zones on map with separate layers for each type
  useEffect(() => {
    if (!zones || !mapInstance.current || !showZones) return;

    // Clear old layers
    Object.values(zoneLayers.current).forEach(layer => {
      if (layer && mapInstance.current.hasLayer(layer)) {
        mapInstance.current.removeLayer(layer);
      }
    });

    const createZoneLayer = (zoneList, style) => {
      if (!Array.isArray(zoneList) || zoneList.length === 0) return null;
      
      const layerGroup = L.layerGroup();
      
      zoneList.forEach((zone) => {
        try {
          const layer = L.geoJSON(zone.geometry, {
            style: () => style,
          });
          layer.bindPopup(`
            <div class="p-2">
              <strong class="text-sm">${zone.name}</strong>
              <br/>
              <span class="text-xs text-gray-500">Type: ${zone.type}</span>
            </div>
          `);
          layerGroup.addLayer(layer);
        } catch (error) {
          console.error('Error adding zone:', zone.name, error);
        }
      });
      
      return layerGroup;
    };

    // Create separate layers for each zone type
    zoneLayers.current.ctr = createZoneLayer(zones.ctr_zones, ZONE_STYLES.ctr);
    zoneLayers.current.tma = createZoneLayer(zones.tma_zones, ZONE_STYLES.tma);
    zoneLayers.current.restricted = createZoneLayer(zones.restricted_zones, ZONE_STYLES.restricted);
    zoneLayers.current.prohibited = createZoneLayer(zones.prohibited_zones, ZONE_STYLES.prohibited);
    zoneLayers.current.danger = createZoneLayer(zones.danger_zones, ZONE_STYLES.danger);

    // Add visible layers to map
    Object.entries(zoneLayers.current).forEach(([type, layer]) => {
      if (layer && layerVisibility[type]) {
        layer.addTo(mapInstance.current);
      }
    });
  }, [zones, showZones]);

  // Toggle individual zone layers visibility
  useEffect(() => {
    if (!mapInstance.current) return;

    Object.entries(zoneLayers.current).forEach(([type, layer]) => {
      if (!layer) return;
      
      if (layerVisibility[type]) {
        if (!mapInstance.current.hasLayer(layer)) {
          layer.addTo(mapInstance.current);
        }
      } else {
        if (mapInstance.current.hasLayer(layer)) {
          mapInstance.current.removeLayer(layer);
        }
      }
    });
  }, [layerVisibility]);

  // Toggle IGN WMS Layer visibility
  useEffect(() => {
    if (!ignWmsLayer.current || !mapInstance.current) return;
    
    if (showIgnLayer) {
      if (!mapInstance.current.hasLayer(ignWmsLayer.current)) {
        ignWmsLayer.current.addTo(mapInstance.current);
      }
    } else {
      if (mapInstance.current.hasLayer(ignWmsLayer.current)) {
        mapInstance.current.removeLayer(ignWmsLayer.current);
      }
    }
  }, [showIgnLayer]);

  // Add initial polygon if provided
  useEffect(() => {
    if (!initialPolygon || !drawnItems.current || !mapInstance.current) return;

    try {
      drawnItems.current.clearLayers();
      
      const layer = L.geoJSON(initialPolygon, {
        style: {
          color: '#0ea5e9',
          fillColor: '#0ea5e9',
          fillOpacity: 0.3,
          weight: 3,
        },
      });
      
      // Add each feature layer to drawnItems for editing capability
      layer.eachLayer((featureLayer) => {
        drawnItems.current.addLayer(featureLayer);
      });
      
      // Fit bounds to polygon
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        mapInstance.current.fitBounds(bounds, { padding: [50, 50] });
      }
    } catch (error) {
      console.error('Error adding initial polygon:', error);
    }
  }, [initialPolygon]);

  // Display initial takeoff point if provided
  useEffect(() => {
    if (!initialTakeoffPoint || !mapInstance.current) return;
    
    // Place the marker at the initial takeoff point
    handleTakeoffPointClick({ lat: initialTakeoffPoint.lat, lng: initialTakeoffPoint.lng });
  }, [initialTakeoffPoint, handleTakeoffPointClick]);

  return (
    <div 
      ref={containerRef}
      className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[9999] bg-white p-3' : ''}`} 
      style={{ height: isFullscreen ? '100vh' : height }}
    >
      {/* Top Bar: Controls + Search (always visible) */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-t-lg border border-slate-200 border-b-0 p-2">
        {/* Layers Panel Toggle — placed FIRST for max visibility */}
        <button
          onClick={() => setLayersPanelOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-all border ${
            layersPanelOpen
              ? 'bg-sky-500 text-white border-sky-600 shadow-md'
              : 'bg-gradient-to-r from-sky-500 to-sky-600 text-white border-sky-600 hover:from-sky-600 hover:to-sky-700 shadow-sm'
          }`}
          data-testid="layers-panel-toggle"
          title="Afficher / masquer les couches & données"
        >
          <Layers className="w-4 h-4" />
          <span>Couches</span>
        </button>

        {/* Address Search */}
        <div className="relative flex-1 min-w-[180px] max-w-[300px]">
          <div className="flex items-center bg-slate-50 rounded border border-slate-200 overflow-hidden">
            <div className="pl-2 text-slate-400">
              {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            </div>
            <input
              type="text"
              placeholder="Rechercher une adresse..."
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              className="w-full px-2 py-1.5 text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
              data-testid="address-search-input"
            />
            {searchAddress && (
              <button onClick={() => { setSearchAddress(''); setSearchResults([]); setShowResults(false); }} className="px-2 text-slate-400 hover:text-slate-600">×</button>
            )}
          </div>
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-48 overflow-y-auto z-50">
              {searchResults.map((result, idx) => (
                <button key={idx} onClick={() => goToAddress(result)} className="w-full px-2 py-2 text-left hover:bg-sky-50 flex items-start gap-2 border-b border-slate-100 last:border-0">
                  <MapPin className="w-3.5 h-3.5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-medium text-slate-900">{result.properties.name || result.properties.label}</div>
                    <div className="text-[10px] text-slate-500">{result.properties.context}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DOM-TOM / Territory quick-jump dropdown */}
        <select
          onChange={(e) => {
            const v = e.target.value;
            if (!v || !mapInstance.current) return;
            const T = {
              metropole: [[46.6, 2.5], 6],
              guadeloupe: [[16.25, -61.55], 10],
              martinique: [[14.65, -61.02], 10],
              guyane: [[3.94, -53.13], 7],
              reunion: [[-21.12, 55.53], 10],
              mayotte: [[-12.83, 45.16], 10],
              'saint-pierre-miquelon': [[46.84, -56.32], 11],
              'polynesie-francaise': [[-17.65, -149.42], 7],
              'nouvelle-caledonie': [[-21.30, 165.50], 7],
              'wallis-et-futuna': [[-13.30, -176.20], 10],
              taaf: [[-49.35, 70.22], 5],
            }[v];
            if (T) mapInstance.current.setView(T[0], T[1]);
            e.target.value = '';
          }}
          className="text-xs border border-slate-200 rounded bg-white px-2 py-1.5 text-slate-700 hover:bg-slate-50 cursor-pointer"
          defaultValue=""
          data-testid="map-territory-jump"
          title="Centrer la carte sur un territoire français"
        >
          <option value="" disabled>📍 Territoires FR…</option>
          <option value="metropole">France métropolitaine</option>
          <option value="guadeloupe">Guadeloupe (971)</option>
          <option value="martinique">Martinique (972)</option>
          <option value="guyane">Guyane (973)</option>
          <option value="reunion">La Réunion (974)</option>
          <option value="mayotte">Mayotte (976)</option>
          <option value="saint-pierre-miquelon">Saint-Pierre-et-Miquelon (975)</option>
          <option value="polynesie-francaise">Polynésie française (987)</option>
          <option value="nouvelle-caledonie">Nouvelle-Calédonie (988)</option>
          <option value="wallis-et-futuna">Wallis-et-Futuna (986)</option>
          <option value="taaf">TAAF (984)</option>
        </select>

        {/* Map Style Toggle */}
        <div className="flex items-center bg-slate-100 rounded p-0.5">
          <button onClick={() => setMapStyle('standard')} className={`px-2 py-1 text-xs font-medium rounded transition-colors ${mapStyle === 'standard' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Standard</button>
          <button onClick={() => setMapStyle('satellite')} className={`px-2 py-1 text-xs font-medium rounded transition-colors ${mapStyle === 'satellite' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Satellite</button>
        </div>

        {/* Fullscreen Button */}
        <button onClick={() => setIsFullscreen(!isFullscreen)} className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors border border-slate-200" data-testid="fullscreen-toggle">
          {isFullscreen ? <><Minimize2 className="w-3.5 h-3.5" /><span>Réduire</span></> : <><Maximize2 className="w-3.5 h-3.5" /><span>Plein écran</span></>}
        </button>

        {/* Takeoff altitude indicator */}
        {takeoffAltitude && (
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
            takeoffAltitude.value === 0 ? 'bg-red-100 text-red-700' : 
            takeoffAltitude.value <= 50 ? 'bg-amber-100 text-amber-700' : 
            'bg-emerald-100 text-emerald-700'
          }`}>
            <span>🛫</span>
            <span>Alt. max: {takeoffAltitude.value === 0 ? 'INTERDIT' : `${takeoffAltitude.value}m`}</span>
          </div>
        )}

        {/* Zoom Level */}
        <span className="text-xs text-slate-400">Z: {currentZoom}</span>
      </div>

      {/* Compact Controls for read-only mode */}
      {compactMode && (
        <div className="flex items-center gap-2 bg-slate-50 border-x border-slate-200 p-1.5">
          <div className="flex items-center bg-white rounded p-0.5 border border-slate-200">
            <button onClick={() => setMapStyle('standard')} className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${mapStyle === 'standard' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Standard</button>
            <button onClick={() => setMapStyle('satellite')} className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${mapStyle === 'satellite' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Satellite</button>
          </div>
          <button onClick={() => setIsFullscreen(!isFullscreen)} className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded border border-slate-200" data-testid="fullscreen-toggle">
            {isFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            <span>{isFullscreen ? 'Réduire' : 'Agrandir'}</span>
          </button>
          <span className="text-[10px] text-slate-400">Z: {currentZoom}</span>
        </div>
      )}

      {/* Map Container - Takes all remaining space */}
      <div className="relative flex-1 min-h-0 rounded-b-lg overflow-hidden border border-slate-200 border-t-0">
        {/* Close button in fullscreen mode - positionné en bas à gauche pour éviter les contrôles Leaflet */}
        {isFullscreen && (
          <button 
            onClick={() => setIsFullscreen(false)} 
            className="absolute bottom-4 left-4 z-[1001] bg-red-500 hover:bg-red-600 rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 transition-colors" 
            title="Quitter le plein écran (Échap)" 
            data-testid="fullscreen-close"
          >
            <X className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Fermer le plein écran</span>
          </button>
        )}

        {/* Map */}
        <div ref={mapRef} className="w-full h-full" data-testid="flight-map" />

        {/* Onboarding hint — landing only (demoMode=false) */}
        <MapOnboardingHint
          visible={!demoMode && !onboardingDismissed && !layersPanelOpen}
          onDismiss={() => setOnboardingDismissed(true)}
          onCtaClick={() => setLayersPanelOpen(true)}
        />

        {/* Floating Layers Panel - Accordion grouped by country/category */}
        {showZones && !compactMode && (
          <MapLayersPanel
            isOpen={layersPanelOpen}
            onClose={() => setLayersPanelOpen(false)}
            showIgnLayer={showIgnLayer} setShowIgnLayer={setShowIgnLayer}
            showDipulLayer={showDipulLayer} setShowDipulLayer={setShowDipulLayer}
            showDfsAirspaceLayer={showDfsAirspaceLayer} setShowDfsAirspaceLayer={setShowDfsAirspaceLayer}
            showBelgiumLayer={showBelgiumLayer} setShowBelgiumLayer={setShowBelgiumLayer}
            showLuxembourgLayer={showLuxembourgLayer} setShowLuxembourgLayer={setShowLuxembourgLayer}
            showNetherlandsLayer={showNetherlandsLayer} setShowNetherlandsLayer={setShowNetherlandsLayer}
            showSwitzerlandLayer={showSwitzerlandLayer} setShowSwitzerlandLayer={setShowSwitzerlandLayer}
            showAustriaLayer={showAustriaLayer} setShowAustriaLayer={setShowAustriaLayer}
            showIcelandLayer={showIcelandLayer} setShowIcelandLayer={setShowIcelandLayer}
            showCzechiaLayer={showCzechiaLayer} setShowCzechiaLayer={setShowCzechiaLayer}
            showSlovakiaLayer={showSlovakiaLayer} setShowSlovakiaLayer={setShowSlovakiaLayer}
            showDenmarkLayer={showDenmarkLayer} setShowDenmarkLayer={setShowDenmarkLayer}
            showIrelandLayer={showIrelandLayer} setShowIrelandLayer={setShowIrelandLayer}
            showEstoniaLayer={showEstoniaLayer} setShowEstoniaLayer={setShowEstoniaLayer}
            showCyprusLayer={showCyprusLayer} setShowCyprusLayer={setShowCyprusLayer}
            showItalyLayer={showItalyLayer} setShowItalyLayer={setShowItalyLayer}
            showItalyDroneRestrictions={showItalyDroneRestrictions}
            setShowItalyDroneRestrictions={setShowItalyDroneRestrictions}
            showSwedenDroneRestrictions={showSwedenDroneRestrictions}
            setShowSwedenDroneRestrictions={setShowSwedenDroneRestrictions}
            showFinlandDroneRestrictions={showFinlandDroneRestrictions}
            setShowFinlandDroneRestrictions={setShowFinlandDroneRestrictions}
            showBelgiumDroneRestrictions={showBelgiumDroneRestrictions}
            setShowBelgiumDroneRestrictions={setShowBelgiumDroneRestrictions}
            showNetherlandsDroneRestrictions={showNetherlandsDroneRestrictions}
            setShowNetherlandsDroneRestrictions={setShowNetherlandsDroneRestrictions}
            showPolandDroneRestrictions={showPolandDroneRestrictions}
            setShowPolandDroneRestrictions={setShowPolandDroneRestrictions}
            showNorwayDroneRestrictions={showNorwayDroneRestrictions}
            setShowNorwayDroneRestrictions={setShowNorwayDroneRestrictions}
            showCroatiaDroneRestrictions={showCroatiaDroneRestrictions}
            setShowCroatiaDroneRestrictions={setShowCroatiaDroneRestrictions}
            showGreeceDroneRestrictions={showGreeceDroneRestrictions}
            setShowGreeceDroneRestrictions={setShowGreeceDroneRestrictions}
            showBulgariaDroneRestrictions={showBulgariaDroneRestrictions}
            setShowBulgariaDroneRestrictions={setShowBulgariaDroneRestrictions}
            showHungaryDroneRestrictions={showHungaryDroneRestrictions}
            setShowHungaryDroneRestrictions={setShowHungaryDroneRestrictions}
            showSloveniaDroneRestrictions={showSloveniaDroneRestrictions}
            setShowSloveniaDroneRestrictions={setShowSloveniaDroneRestrictions}
            showSpainLayer={showSpainLayer} setShowSpainLayer={setShowSpainLayer}
            showOpenAipLayer={showOpenAipLayer} setShowOpenAipLayer={setShowOpenAipLayer}
            showDjiLayer={showDjiLayer} setShowDjiLayer={setShowDjiLayer}
            showNotamsLayer={showNotamsLayer} setShowNotamsLayer={setShowNotamsLayer}
            showEd269Layer={showEd269Layer} setShowEd269Layer={setShowEd269Layer}
            ed269Countries={ed269Country} toggleEd269Country={toggleEd269Country}
            setEd269Country={setEd269Country}
            ed269Filter={ed269Filter} setEd269Filter={setEd269Filter}
            ed269Available={ed269Available} ed269Loading={ed269Loading}
            layerVisibility={layerVisibility} setLayerVisibility={setLayerVisibility}
            showNatura2000Wms={showNatura2000Wms} setShowNatura2000Wms={setShowNatura2000Wms}
            showEnvironmentalLayers={showEnvironmentalLayers} setShowEnvironmentalLayers={setShowEnvironmentalLayers}
            loadingEnvZones={loadingEnvZones}
            showHospitalsLayer={showHospitalsLayer} setShowHospitalsLayer={setShowHospitalsLayer}
            showAirportsLayer={showAirportsLayer} setShowAirportsLayer={setShowAirportsLayer}
            showAirportsPPPLayer={showAirportsPPPLayer} setShowAirportsPPPLayer={setShowAirportsPPPLayer}
            showHelistationsLayer={showHelistationsLayer} setShowHelistationsLayer={setShowHelistationsLayer}
          />
        )}
        
        {/* NOTAM overlay — floating card top-right when the layer is on.
            Wraps the existing NotamPanel so we get importance colours,
            expand/collapse, and API fallback logic for free. Hidden on
            small screens by default so it doesn't obscure the map;
            users can still toggle from the layers panel. */}
        {showNotamsLayer && !compactMode && mapCenterForNotams && (
          <div
            className="absolute top-3 right-3 z-[1000] w-80 max-w-[calc(100%-24px)] max-h-[65%] overflow-y-auto rounded-lg shadow-xl bg-white border border-slate-200 hidden md:block"
            data-testid="flightmap-notam-overlay"
          >
            <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200 rounded-t-lg">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">NOTAMs zone</span>
              <button
                onClick={() => setShowNotamsLayer(false)}
                aria-label="Masquer les NOTAMs"
                data-testid="flightmap-notam-close"
                className="text-slate-400 hover:text-slate-700 text-lg leading-none px-1"
              >
                ×
              </button>
            </div>
            <div className="p-2">
              <NotamPanel lat={mapCenterForNotams.lat} lng={mapCenterForNotams.lng} />
            </div>
          </div>
        )}

        {/* Loading Indicator for popups */}
        {(airspacePopupLoading || dipulPopupLoading) && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg px-3 py-1.5 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />
            <span className="text-xs text-slate-600">Chargement...</span>
          </div>
        )}

        {/* Map Attribution */}
        <div className="absolute bottom-0.5 right-0.5 text-[8px] text-slate-500 bg-white/80 px-1 py-0.5 rounded z-[1000]">
          © Drone Maps · Données officielles européennes
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightMap;
